/**
 * `WorkflowDocument.ts` — the typed in-memory model of a per-project workflow
 * and its structural validator.
 *
 * The workflow is STORED as one structured YAML document (`workflow.yaml`, see
 * `WorkflowYaml.ts` for the codec and `WORKFLOW_FILE_SPEC_documentation.md` for the format).
 * This module owns the parsed shape: identity, header, groups, steps with
 * their authored fields, trigger buttons and prepared scripts,
 * and the runtime `workflow_state`. There is no raw text and no line indexing
 * here — the MD-like text the editor shows is a projection rendered from this
 * model (`WorkflowAuthoredView.ts`), never the document itself.
 *
 * Presence semantics: authored string fields are `string | null`. `null` means
 * the field is absent (the authored view does not render its label); `""`
 * means present-but-empty (the view renders the bare `Label:` line with an
 * editor hint). The template seeds present-but-empty fields on purpose.
 *
 * Pure logic — no DOM, no Vue, no IOC. The only cross-layer import is the
 * tool-id map (`@ai/registry/agentToolNames`), mirroring Unity where the
 * workflow model references `Campfire.AI.AgentToolNamesExtensions`.
 * `validate(document, catalog?)` is still headless: the optional catalog is a
 * plain snapshot the file model builds from the project.
 */

import { AgentToolNames } from "@ai/registry/agentToolNames";
import {
  getScriptCommandSpec,
  isAuthorableScriptCommand,
  isScriptCommandCatalogReady,
  isScriptContextTool,
  isStructuralScriptCommand,
  type ScriptCommandArg,
} from "@ai/registry/scriptCommandCatalog";
import { normalizeProductNameKey } from "@models/data/productDocumentDto";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * What KIND of workflow this is — which schema it follows and how a runner is
 * expected to execute it. `procedure` is the only kind so far; a strict enum
 * from the start so a second kind arrives as a decision rather than as a typo
 * that silently plays as a procedure.
 */
export const WORKFLOW_TYPES = ["procedure"] as const;
export type WorkflowType = (typeof WORKFLOW_TYPES)[number];

/**
 * Format version the codec writes into new files.
 *
 * Deliberately NOT bumped automatically on an existing file: once an external
 * system emits or consumes a version it is frozen, and silently rewriting it
 * would make a file claim conformance to a format it was never checked
 * against. The file model stamps this only when the key is absent.
 */
export const WORKFLOW_FORMAT_VERSION = "0.2";

/** Normalizes a workflow type; null when unrecognized (strict enum). */
export function normalizeWorkflowType(value: string): WorkflowType | null {
  const key = value.trim().toLowerCase();
  return (WORKFLOW_TYPES as readonly string[]).includes(key) ? (key as WorkflowType) : null;
}

/**
 * Canonical script triggers. Machine-owned data, so these are STRICT values —
 * an unrecognised value is a validation error naming the typo, never a silent
 * fallback.
 *
 * There is no completion trigger: a step's script runs when the step becomes
 * current, and Next is pure navigation. A second script that had to finish
 * before the learner could move on made Next wait on something they could not
 * see, and every failure mode of that wait (script errors, the learner
 * navigating mid-play, a second press) needed its own escape hatch.
 */
export const SCRIPT_TRIGGERS = ["on_start", "manual"] as const;
export type WorkflowScriptTrigger = (typeof SCRIPT_TRIGGERS)[number];

/**
 * The CONCRETE capabilities a run may leave open to the learner — what the
 * editor shell actually gates on.
 *
 *  • `ai_input` — Spark's composer. The learner can ask questions mid-run; the
 *    Guide answers them without losing their place.
 *  • `tools`    — the scene tool palette. RESERVED in this release: a run hands
 *    the presenter role to Spark, which withdraws every tool from the learner,
 *    so it opens nothing (AI-1426). Still parsed and resolved as before.
 *
 * A LIST rather than the single boolean this replaced (2026-08-21,
 * user-directed): "may the learner interact" was one switch over two unrelated
 * capabilities, and the common case — let them ask, but keep them out of the
 * scene — could not be expressed at all. Absent or empty means neither, which
 * is the same guided default the boolean's `false` had.
 */
export const USER_INPUT_KINDS = ["ai_input", "tools"] as const;
export type WorkflowUserInputKind = (typeof USER_INPUT_KINDS)[number];

/**
 * The wildcard member: everything, including whatever kind is added next.
 *
 * ⚠ It is STORED verbatim and expanded at every READ
 * ({@link resolveUserInputAllowed}), never unfolded into the concrete kinds at
 * parse time — and that is the whole reason it is a member rather than a
 * shorthand the codec rewrites. Unfolding would FREEZE a workflow at today's
 * vocabulary, so a capability added later would silently not be covered by a
 * list whose author wrote `all`; it would also rewrite their `[all]` into a
 * two-item list on the next save, for a key they never touched.
 */
export const USER_INPUT_ALL = "all";

/**
 * What may be WRITTEN in a `user_input_allowed` list: a concrete kind, or the
 * wildcard. Distinct from {@link USER_INPUT_KINDS} on purpose — the stored
 * vocabulary is wider than the resolved one, and only this list belongs in an
 * error message or a schema.
 */
export const USER_INPUT_ALLOWANCES = [...USER_INPUT_KINDS, USER_INPUT_ALL] as const;
export type WorkflowUserInputAllowance = (typeof USER_INPUT_ALLOWANCES)[number];

/** Normalizes a stored `user_input_allowed` member, or null when unknown. */
export function normalizeUserInputAllowance(value: string): WorkflowUserInputAllowance | null {
  const key = value.trim().toLowerCase();
  return (USER_INPUT_ALLOWANCES as readonly string[]).includes(key)
    ? (key as WorkflowUserInputAllowance)
    : null;
}

/**
 * Canonical revert modes — how long a script's scene changes stay visible.
 * Applied by `WorkflowScriptRunner` / `WorkflowRevertState` during playback.
 * The default first, then shortest-lived first: `none` puts the scene back
 * when playback ends, `on_action_finish` when the script ends,
 * `on_step_change` when the learner leaves the step it ran on, and
 * `on_scene_change` when they leave the scene it ran in.
 *
 * `on_step_change` is WEB-ONLY (2026-10-29): Unity's `k_scriptReverts` has the
 * other three, and the desktop client reads it as an unknown revert. When
 * re-diffing `WorkflowDocument.cs`, port it there; do not drop it here.
 */
export const SCRIPT_REVERTS = ["none", "on_action_finish", "on_step_change", "on_scene_change"] as const;
export type WorkflowScriptRevert = (typeof SCRIPT_REVERTS)[number];

/**
 * Canonical step-trigger types (a step's `triggers:` buttons). `button` is
 * the only type so far: a labelled button the Workflow panel shows while the
 * step is current, jumping the learner to another step or group.
 */
export const STEP_TRIGGER_TYPES = ["button"] as const;
export type WorkflowStepTriggerType = (typeof STEP_TRIGGER_TYPES)[number];

/**
 * What KIND of media a step carries. Strict enum from the start: the two
 * render as different elements and only one of them has a timeline, so a
 * third kind has to be a decision rather than a typo that silently plays as
 * an image.
 */
export const MEDIA_TYPES = ["image", "video"] as const;
export type WorkflowMediaType = (typeof MEDIA_TYPES)[number];

/** Normalizes a media type; null when unrecognized (strict enum). */
export function normalizeMediaType(value: string): WorkflowMediaType | null {
  const key = value.trim().toLowerCase();
  return (MEDIA_TYPES as readonly string[]).includes(key) ? (key as WorkflowMediaType) : null;
}

/**
 * Whether the media may be CROPPED to fill the frame it is shown in.
 *
 * `fill` — the default, and what an absent key means — leaves the framing to
 * the SURFACE: the stage is the shape that surface's design chose, and one
 * whose stage is not the media's own shape may crop to fill it. Today that is
 * exactly one place, a video in the full-screen presentation on a landscape
 * phone, where `object-fit: cover` is what "video full screen" was asked for.
 *
 * `contain` is a CONTRACT the other way: this media is never cropped, in any
 * surface, present or future. All of it is always visible, letterboxed where
 * the stage's shape differs.
 *
 * ⚠ It says nothing about the stage's SHAPE. A `contain` media in the docked
 * card is still shown in that panel's 16:9 stage — letterboxed, not reshaping
 * the panel. The stage's aspect belongs to the surface: it is what keeps a
 * magnetically docked pair's bottom edges level, and the centred presentation's
 * band arithmetic (`--wfm-expanded-bottom` / `--wfm-w` in `WorkflowMediaView`)
 * is built on 16:9 throughout. An author asking not to be cropped is not asking
 * for a differently-shaped panel, and threading an intrinsic aspect that only
 * arrives on `loadedmetadata` into properties the expand morph animates is a
 * separate change with its own risks.
 */
export const MEDIA_FITS = ["contain", "fill"] as const;
export type WorkflowMediaFit = (typeof MEDIA_FITS)[number];

/** Normalizes a media fit; null when unrecognized (strict enum, like the type). */
export function normalizeMediaFit(value: string): WorkflowMediaFit | null {
  const key = value.trim().toLowerCase();
  return (MEDIA_FITS as readonly string[]).includes(key) ? (key as WorkflowMediaFit) : null;
}

/**
 * What has to happen before a step's Next button becomes available. A step
 * with no `completion_condition` has an immediately available Next, which is
 * every step written before this key existed.
 *
 * It gates the learner's FIRST arrival only — returning to a step they have
 * already visited is never gated again (`WorkflowRunModel.armCompletionGate`),
 * because the condition is about what they see before moving on the first
 * time, not a toll on every pass through.
 *
 * Strict enum, like every other machine value: each kind is satisfied by a
 * completely different event — a clock, the step's own script settling, and
 * the step's own video reaching its end — so a fourth has to arrive as a
 * decision rather than as a typo that silently behaves like one of these.
 */
export const COMPLETION_CONDITIONS = ["delay", "script_finished", "video_finished"] as const;
export type WorkflowCompletionConditionType = (typeof COMPLETION_CONDITIONS)[number];

/** Normalizes a completion-condition type; null when unrecognized (strict enum). */
export function normalizeCompletionCondition(
  value: string,
): WorkflowCompletionConditionType | null {
  const key = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  return (COMPLETION_CONDITIONS as readonly string[]).includes(key)
    ? (key as WorkflowCompletionConditionType)
    : null;
}

/**
 * Ceiling on an authored completion delay, in seconds. Same five minutes
 * `wait_for` clamps to, and for the same reason: a delay that holds the Next
 * button for an hour is a typo rather than an intention, but the step still
 * has to become passable, so this clamps rather than refusing.
 */
export const MAX_COMPLETION_DELAY_SECONDS = 300;

/** File extensions that make an untyped media URL a video rather than an image. */
const K_VIDEO_EXTENSIONS: ReadonlySet<string> = new Set([
  "mp4",
  "m4v",
  "webm",
  "ogv",
  "mov",
  "mpd",
  "m3u8",
]);

const K_RESPONSE_STEP_KEYWORDS: ReadonlyArray<string> = [
  "wait",
  "listen",
  "answer",
  "respond",
  "reply",
  "confirm",
  "ready when",
];

/**
 * Lowercased wire ids of every tool the web registry knows about. Unity checks
 * `AgentToolNamesExtensions.FromId(tool) == -1`; the web equivalent is a
 * case-insensitive set lookup over `AgentToolNames`.
 */
const K_VALID_TOOL_IDS: ReadonlySet<string> = new Set(
  Object.values(AgentToolNames).map((value) => value.toLowerCase()),
);

// ---------------------------------------------------------------------------
// String helpers (C# BCL semantics)
// ---------------------------------------------------------------------------

export function isNullOrWhiteSpace(value: string | null | undefined): boolean {
  return value == null || value.trim().length === 0;
}

export function equalsIgnoreCase(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

function containsIgnoreCase(value: string, search: string): boolean {
  return value.toLowerCase().includes(search.toLowerCase());
}

/** C# `string.Trim(params char[])`. */
export function trimChars(value: string, chars: string): string {
  let start = 0;
  while (start < value.length && chars.includes(value.charAt(start))) start++;
  let end = value.length;
  while (end > start && chars.includes(value.charAt(end - 1))) end--;
  return value.slice(start, end);
}

// ---------------------------------------------------------------------------
// Normalizers
// ---------------------------------------------------------------------------

/**
 * Normalizes a trigger written by a human, an agent, or an API caller to its
 * canonical value. Accepts the spaced/hyphenated spellings and the bare
 * keywords callers pass to {@link WorkflowStep.findScriptByTrigger} ("start",
 * "completion"). Returns null when nothing matches, so the caller decides
 * between a default and a validation error.
 */
export function normalizeScriptTrigger(value: string): WorkflowScriptTrigger | null {
  const key = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (key.length === 0) return null;
  if (key === "on_start" || key === "start") return "on_start";
  if (key === "manual") return "manual";
  return null;
}

/** Normalizes a revert mode to its canonical value; null when unrecognized. */
export function normalizeScriptRevert(value: string): WorkflowScriptRevert | null {
  const key = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (key.length === 0) return null;
  return (SCRIPT_REVERTS as readonly string[]).includes(key)
    ? (key as WorkflowScriptRevert)
    : null;
}

/** Normalizes a step-trigger type; null when unrecognized (strict enum). */
export function normalizeStepTriggerType(value: string): WorkflowStepTriggerType | null {
  const key = value.trim().toLowerCase();
  return (STEP_TRIGGER_TYPES as readonly string[]).includes(key)
    ? (key as WorkflowStepTriggerType)
    : null;
}

// ---------------------------------------------------------------------------
// Model types
// ---------------------------------------------------------------------------

/**
 * One executable step of a prepared workflow script. Either a verbatim say
 * line (`sayText` set) or a tool call (`tool` + `args`). An entry the codec
 * could not shape into either KEEPS its raw YAML value in `invalidRaw` so a
 * re-serialize preserves it verbatim — `validate()` names it instead of the
 * write silently deleting it.
 */
export class WorkflowScriptStep {
  tool = "";
  /** Tool arguments as a real (nested, typed) object; null when none. */
  args: Record<string, unknown> | null = null;
  sayText = "";
  /** Raw YAML value of an entry that is neither a say nor a tool step. */
  invalidRaw: unknown = undefined;

  get isSay(): boolean {
    return this.sayText.length > 0;
  }

  get isInvalid(): boolean {
    return this.invalidRaw !== undefined;
  }

  /**
   * The arguments as the compact JSON string the executor hands to a tool.
   * Derived from {@link args} so the two can never disagree; empty for a say
   * step or a tool with no arguments.
   */
  get argsJson(): string {
    if (this.args === null || Object.keys(this.args).length === 0) return "";
    return JSON.stringify(this.args);
  }
}

/**
 * A fully self-contained script from a step's `scripts:` list. The workflow
 * document is the single source of truth for these: steps carry the exact
 * tool calls, so playback needs no scene-stored actions or any other DB
 * record.
 */
export class WorkflowScript {
  name = "";
  id = "";
  trigger: WorkflowScriptTrigger = "manual";
  revert: WorkflowScriptRevert = "none";
  steps: WorkflowScriptStep[] = [];
  /**
   * The raw `trigger` / `revert` text when it did not normalize. Kept so
   * `validate()` can name the typo AND so the value round-trips VERBATIM
   * through a rewrite — the error must survive instead of being silently
   * normalized into the safe default.
   */
  invalidTrigger = "";
  invalidRevert = "";
  /**
   * Raw YAML value of a `scripts:` entry that is not a mapping at all. Such an
   * entry is preserved byte-for-value through re-serialization and reported by
   * `validate()` — never dropped, never read as "no script".
   */
  invalidRaw: unknown = undefined;

  get isInvalid(): boolean {
    return this.invalidRaw !== undefined;
  }

  get isStartScript(): boolean {
    return this.trigger === "on_start";
  }
}

/**
 * One trigger button of a step's `triggers:` list. The Workflow panel
 * renders one button per entry while the step is current; pressing it
 * navigates to `goTo` (a group target resolves to the group's first step)
 * WITHOUT completing the current step.
 *
 * In the authored view these render as numbered `Button "<label>" -> <target>`
 * lines under a `Triggers:` label; a typed line that does not match that
 * grammar round-trips verbatim through `invalidText` so `validate()` can name
 * it instead of the line being silently dropped.
 */
export class WorkflowStepTrigger {
  type: WorkflowStepTriggerType = "button";
  label = "";
  goTo = "";
  /** Verbatim authored line when it did not match the trigger grammar. */
  invalidText = "";
  /** The raw type word when it is not a known trigger type. */
  invalidType = "";
  /** Raw YAML value of a `triggers:` entry that is not a mapping. */
  invalidRaw: unknown = undefined;

  get isInvalid(): boolean {
    return this.invalidRaw !== undefined;
  }
}

/**
 * One step of the workflow. Steps are keyed by NAME (handoffs and trigger
 * targets reference it), with an optional stable `stepId` that makes an API
 * rename safe. Field presence follows the module-header convention:
 * `null` = absent, `""` = present-but-empty.
 */
export class WorkflowStep {
  name = "";
  /** Stable machine id, minted by an agent or an API write. Empty until then. */
  stepId = "";
  /**
   * Whether this step is PARKED: it stays in the file, keeps its content, and
   * is still edited and validated, but no run ever reaches it. Absent (null) =
   * false, so a workflow that says nothing behaves exactly as it did before
   * this key existed.
   *
   * True means invisible to PLAYBACK, not deleted — which is the whole point of
   * having it rather than cutting the step out: the author keeps a step they
   * are not ready to ship, or takes a broken one out of the way without losing
   * the writing. Every question a run asks — where do I start, what is next,
   * how many steps are there, can I jump here, which trigger buttons do I show
   * — is asked of {@link getPlayableSteps} and never of `document.steps`.
   */
  disabled: boolean | null = null;
  /**
   * Which scene this step is about: display name, 1-based index, or scene id.
   * Empty/absent = plays wherever the learner already is.
   */
  scene: string | null = null;
  expectedDuration: string | null = null;
  /**
   * What this step is ABOUT, in one line, for the guide — never shown to the
   * learner. It is what the agent matches when the learner asks for a step by
   * describing it ("the bit about the bracket") instead of naming it, which is
   * a lookup the step's NAME alone often cannot answer.
   *
   * Same meaning as {@link WorkflowDocument.description} on the header: a
   * summary of the thing, not its content. The learner-facing copy is
   * {@link stepInstructions}, and the two are deliberately separate — the
   * sentence that helps a model recognise a step and the paragraph a learner
   * is asked to follow are different pieces of writing, and one field could
   * only serve them by being wrong for one of the two.
   *
   * `step_context` in the file since 2026-08-27, renamed from `description`.
   * The old name was the header's word for the same idea one level up, so the
   * two read as one field the format happened to allow twice — while what a
   * step carries is the CONTEXT the guide needs to place it, and its sibling
   * `step_instructions` is what the learner reads. Naming the pair together is
   * what makes "which of these two am I writing?" answerable from the key.
   */
  stepContext: string | null = null;
  /**
   * The copy the play panel shows under the step name — what the learner is
   * asked to do here. Empty or absent means the panel shows nothing, so there
   * is no separate shown/hidden switch: instructions have content of their own
   * to be empty, unlike a button.
   *
   * A script's `say:` can name this text instead of repeating it
   * ({@link SAY_STEP_INSTRUCTIONS_TOKEN}), so the panel and the spoken line
   * cannot drift apart.
   */
  stepInstructions: string | null = null;
  /**
   * The Back button's OPTIONAL label. Empty or absent is not "no button" — it
   * is a button with no words, drawn as the arrow alone. The button's presence
   * is navigation's call, never the author's; see {@link getStepBackButtonLabel}.
   */
  backButton: string | null = null;
  /** The Next button's label; empty or absent hides the button. */
  nextButton: string | null = null;
  /**
   * Where this step's Next goes: a step or group name, resolved exactly like a
   * trigger's `go_to`. Null (absent or empty — {@link normalizeNextTarget}) means
   * the next step in the file, which is how every step behaved before this key
   * existed. It changes only the DESTINATION: Next still completes the step,
   * still waits for its `completion_condition`, and on an ending still finishes
   * the run. {@link resolveNextStep} is the one place it is read.
   */
  nextTarget: string | null = null;
  /**
   * Whether this step ENDS the run: pressing Next completes it and leaves play
   * mode instead of advancing. Absent (null) = false, so a workflow that says
   * nothing behaves exactly as it did before this key existed.
   *
   * Deliberately not "is this the last step" — the two are different questions.
   * A workflow can branch, so the step the learner finishes on may sit anywhere
   * in the list, and the last step in document order may be a detour nobody is
   * meant to leave from. The author says which one is the ending.
   */
  isCompletionStep: boolean | null = null;
  /**
   * What has to happen before the learner may press Next, or null when Next is
   * available as soon as the step arrives (the default, and how every step
   * behaved before this key existed).
   */
  completionCondition: WorkflowStepCompletionCondition | null = null;
  /** Optional scene attachment — null when the step points at nothing. */
  attachment: WorkflowStepAttachment | null = null;
  /**
   * Optional camera framing — where the viewport flies when this step is
   * activated. Null (the default) flies to the step's SCENE's saved view
   * instead, so every step of a run opens on a view (AI-1424).
   */
  stepView: WorkflowStepView | null = null;
  /** Optional image or video shown beside the play panel — null when none. */
  media: WorkflowStepMedia | null = null;
  /**
   * Per-step override of the workflow's `user_input_allowed`; null = inherit.
   * An empty LIST is not the same as null — it is an explicit "nothing here",
   * which is how a mostly-open workflow closes one step down.
   */
  userInputAllowed: WorkflowUserInputAllowance[] | null = null;
  /** Members of `user_input_allowed` this client does not recognise, kept so a
   *  round trip cannot silently drop them; reported by `validate`. */
  invalidUserInputAllowed: string[] = [];
  triggers: WorkflowStepTrigger[] = [];
  scripts: WorkflowScript[] = [];
  /**
   * Name of the group this step sits under, or "" when ungrouped. Steps of
   * one group must be contiguous in {@link WorkflowDocument.steps} — the
   * authored view renders group headings positionally, and play order is the
   * flat step order. `validate()` errors on a non-contiguous group.
   */
  groupName = "";

  findScriptById(scriptId: string): WorkflowScript | null {
    if (isNullOrWhiteSpace(scriptId)) return null;
    return this.scripts.find((script) => equalsIgnoreCase(script.id, scriptId.trim())) ?? null;
  }

  /**
   * Finds a script by trigger. The argument is normalized, so both the
   * canonical values and the bare keywords callers pass ("start",
   * "completion") resolve — `play_workflow_script` takes the latter.
   */
  findScriptByTrigger(triggerKeyword: string): WorkflowScript | null {
    const trigger = normalizeScriptTrigger(triggerKeyword);
    if (trigger === null) return null;
    return this.scripts.find((script) => script.trigger === trigger) ?? null;
  }
}

/**
 * Where a step points in the scene. Optional — a step with no attachment shows
 * no connector at all, which is the default.
 *
 * `part` is a slash-joined DISPLAY-NAME path (`Robodog/Fan`) rather than the
 * index path the AI tools use (`0,1,2`): this value is rendered in the authored
 * view, where an index path says nothing to the author. `point` is an optional
 * exact world point ON that part — captured from a viewport pick — for when the
 * part's centre is not the thing being pointed at.
 */
export class WorkflowStepAttachment {
  part = "";
  point: { x: number; y: number; z: number } | null = null;

  get isEmpty(): boolean {
    return this.part.trim().length === 0 && this.point === null;
  }
}

/**
 * A step's camera framing: where the viewport flies when the step is activated.
 * Optional — a step without one opens on its scene's saved view during a run,
 * even when the learner moved the camera (AI-1424; before that it left the
 * camera wherever it was). Authoring never moves the camera.
 *
 * ## The pair, and why it is not a rotation
 *
 * `position` is the camera, `pivot` is the world point it looks at and orbits.
 * That pair is the WHOLE view on both clients — the orbit rig derives the
 * transform from target + angles + radius, so the zoom is `|position - pivot|`
 * and there is no separate rotation to store. It is deliberately the same shape
 * as a SCENE's authored view ({@link SceneViewState}), because this is the same
 * thing one level down: the desktop client persists that pair per scene and
 * flies to it on a scene change, and a step view is that behaviour per step.
 *
 * ⚠ Do NOT rename `pivot` to `rotation`, however tempting. Unity's Dynamo
 * attribute for the scene-level pivot IS misleadingly called
 * `SceneViewRotation`, and `SceneViewState`'s header documents at length that
 * the name is a lie and that running the value through a rotation converter is
 * the one mistake the feature invites. Propagating that name into a web-first
 * format would re-import the trap.
 *
 * ## Space
 *
 * Both vectors are **world-space, UNITY axes, metres, scene-relative** — the
 * same space a scene row persists, so the value is meaningful to the desktop
 * client and survives an XR console placement moving the stage. The conversion
 * to three-space happens once, at the renderer boundary in
 * `runtime/camera/sceneViewCameraHost.ts`; nothing in this layer converts.
 *
 * Both halves are optional in the FILE (an agent can send half a block, and a
 * hand-edited file can hold one), which is what `validate()` warns about —
 * {@link resolveStepView} then fails open and the step simply does not move the
 * camera.
 */
export class WorkflowStepView {
  position: { x: number; y: number; z: number } | null = null;
  pivot: { x: number; y: number; z: number } | null = null;

  get isEmpty(): boolean {
    return this.position === null && this.pivot === null;
  }
}

/**
 * The step's EFFECTIVE camera framing, or null when the step does not move the
 * camera.
 *
 * **Fails open by construction**, the rule {@link resolveStepCompletionCondition}
 * and {@link resolveUserInputAllowed} already follow: a half-written block —
 * one vector present and the other absent — resolves to null rather than to a
 * pose with an invented half, so it is reported by `validate()` and never flies
 * the camera somewhere nobody asked for. A camera sent to a fabricated pivot is
 * far worse than one that stays put.
 *
 * The one lookup, so the run model, the agent tool and any later editor
 * affordance cannot come to different answers about the same step.
 */
export function resolveStepView(
  step: WorkflowStep,
): {
  readonly position: readonly [number, number, number];
  readonly pivot: readonly [number, number, number];
} | null {
  const view = step.stepView;
  if (view === null || view.position === null || view.pivot === null) return null;
  return {
    position: [view.position.x, view.position.y, view.position.z],
    pivot: [view.pivot.x, view.pivot.y, view.pivot.z],
  };
}

/**
 * What has to happen on a step before its Next button becomes available.
 * Optional — a step without one has an immediately available Next.
 *
 * Three kinds, gated by different things: `delay` by a clock started when the
 * step arrives, `script_finished` by the step's own `on_start` script settling,
 * `video_finished` by the step's own media video reaching its end (its authored
 * trim's end, or the file's).
 *
 * Each of the latter two is satisfied IMMEDIATELY when the thing it names is
 * not there — a `script_finished` on a step with no on-start script, a
 * `video_finished` on a step whose media is an image or absent — rather than
 * trapping the learner on a step they can never leave. `validate()` warns
 * instead, which is the rule the whole format follows: a warning is work for
 * the builder, never a wall for the learner.
 */
export class WorkflowStepCompletionCondition {
  /** The kind, when the author wrote a recognised one. Null while `invalidType` holds a typo. */
  type: WorkflowCompletionConditionType | null = null;
  /**
   * `delay` only: how long after the step arrives Next becomes available, as
   * authored. Null when absent — which is a validation ERROR on a delay
   * condition, since a delay with no length is a gate that would never say
   * when it opens.
   */
  delaySeconds: number | null = null;
  /** The raw type word when it did not normalize (round-trips verbatim). */
  invalidType = "";

  get isEmpty(): boolean {
    return this.type === null && this.invalidType.length === 0 && this.delaySeconds === null;
  }
}

/**
 * The step's EFFECTIVE completion gate, or null when Next is available
 * immediately.
 *
 * **Fails open by construction**: a block the author got wrong — an
 * unrecognised type, a delay with no length or a non-positive one — resolves to
 * null, so it is reported by `validate()` and never blocks the button. The one
 * lookup, so the play panel's disabled state, the run model's gate and the
 * agent's `direction=next` cannot come to different answers about the same
 * step.
 *
 * A delay is clamped to {@link MAX_COMPLETION_DELAY_SECONDS} here rather than
 * at the point it is armed, so every reader sees the number that will actually
 * be waited.
 */
export function resolveStepCompletionCondition(
  step: WorkflowStep,
): { readonly kind: WorkflowCompletionConditionType; readonly delaySeconds: number } | null {
  const condition = step.completionCondition;
  if (condition === null || condition.type === null) return null;

  // Everything but `delay` is opened by an EVENT rather than by a clock, so
  // there is no length to validate and nothing to clamp. Written as "not a
  // delay" rather than as a list of the event kinds, so a fourth kind added to
  // the enum resolves rather than silently falling through to null.
  if (condition.type !== "delay") return { kind: condition.type, delaySeconds: 0 };

  const seconds = condition.delaySeconds;
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return null;
  return { kind: "delay", delaySeconds: Math.min(seconds, MAX_COMPLETION_DELAY_SECONDS) };
}

/**
 * An image or a video the step shows alongside its prose, addressed by URL.
 * Optional — most steps carry none, and a step with media renders a SECOND
 * panel beside the play panel rather than embedding the media in it.
 *
 * The URL is the whole reference: an external `https://…` is used verbatim,
 * and media uploaded through the Asset Library's Media tab is addressed by the
 * `campfire://media/…` URI that tab hands out (the play panel resolves it
 * against customer storage). Nothing here knows about either — a URL is a
 * string, and the panel owns resolving it.
 *
 * `transcript`, `startTime`, `endTime`, `loop` and `autoplay` only mean something for a
 * video; `validate()` warns when they sit on an image rather than silently
 * ignoring them, since the author clearly meant the step to play something.
 */
export class WorkflowStepMedia {
  /**
   * The kind, when the author stated it. `null` INFERS it from the URL's
   * extension ({@link resolveMediaType}) — an authored `media: { url: …mp4 }`
   * should just work, and an inferred value can never disagree with an
   * explicit one because it is only consulted in its absence.
   */
  type: WorkflowMediaType | null = null;
  /**
   * Whether this media may be cropped to fill its frame
   * ({@link MEDIA_FITS}). `null` is the default and means `fill` — resolved in
   * one place, {@link resolveMediaFit}.
   */
  fit: WorkflowMediaFit | null = null;
  url = "";
  /** Display label for the media panel's header; empty falls back to the step name. */
  name = "";
  description = "";
  /** Video only: the spoken content as text, for the guide and for accessibility. */
  transcript = "";
  /**
   * Video only: where playback starts / stops, as the AUTHORED timecode text
   * (`12`, `1:05`, `0:01:05.5`). Kept verbatim rather than as seconds so the
   * value the author typed is the value the file holds;
   * {@link parseMediaTimecodeSeconds} is the one place it becomes a number.
   */
  startTime = "";
  endTime = "";
  /**
   * Video only: whether the clip plays CONTINUOUSLY, restarting from its start
   * (the authored one when trimmed) each time it reaches its end.
   *
   * `null` is the default and means play once — resolved in one place,
   * {@link resolveMediaLoop}. `false` is the same behaviour written down, and
   * round-trips as such rather than being normalized away.
   *
   * ⚠ A looping clip still reports ONE completed playback, so a
   * `completion_condition: video_finished` opens after the first full pass and
   * the learner is not held for a second lap. The gate only opens once, so
   * every later lap reports into a gate that is already satisfied.
   */
  loop: boolean | null = null;
  /**
   * Video only: start playback as soon as the step arrives.
   *
   * `null` is the default and means wait for either the learner or an authored
   * `play_video` script command. `false` states that same choice explicitly and
   * round-trips rather than being normalized away.
   */
  autoplay: boolean | null = null;
  /** The raw type word when it did not normalize (round-trips verbatim). */
  invalidType = "";
  /** The raw fit word when it did not normalize (round-trips verbatim). */
  invalidFit = "";

  get isEmpty(): boolean {
    return (
      this.url.trim().length === 0 &&
      this.name.trim().length === 0 &&
      this.description.trim().length === 0 &&
      this.transcript.trim().length === 0 &&
      this.startTime.trim().length === 0 &&
      this.endTime.trim().length === 0 &&
      this.loop === null &&
      this.autoplay === null &&
      this.invalidType.length === 0 &&
      this.invalidFit.length === 0 &&
      this.type === null &&
      this.fit === null
    );
  }
}

/**
 * The media's effective kind: what the author stated, else what the URL's
 * extension says, else an image. A query-string or fragment is stripped first
 * — a signed `…/clip.mp4?X-Amz-…` is still a video.
 */
export function resolveMediaType(media: WorkflowStepMedia): WorkflowMediaType {
  if (media.type !== null) return media.type;

  const path = media.url.trim().split(/[?#]/, 1)[0];
  const dot = path.lastIndexOf(".");
  if (dot < 0) return "image";
  return K_VIDEO_EXTENSIONS.has(path.slice(dot + 1).toLowerCase()) ? "video" : "image";
}

/**
 * The media's effective fit: what the author stated, else `fill`. The one place
 * an absent `fit` becomes a decision, so the play panel, the schema and any
 * future surface cannot disagree about what the default is.
 */
export function resolveMediaFit(media: WorkflowStepMedia): WorkflowMediaFit {
  return media.fit ?? "fill";
}

/**
 * The media's effective loop: what the author stated, else play once. The one
 * place an absent `loop` becomes a decision, so the play panel, the schema and
 * any future surface cannot disagree about what the default is.
 *
 * Callers still have to check the KIND: this answers only what the author
 * asked for, and looping an image means nothing (`validate()` warns).
 */
export function resolveMediaLoop(media: WorkflowStepMedia): boolean {
  return media.loop ?? false;
}

/** The media's effective autoplay: absent means wait for an explicit play. */
export function resolveMediaAutoplay(media: WorkflowStepMedia): boolean {
  return media.autoplay ?? false;
}

/**
 * Whether this step actually has a video for the learner to watch — the one
 * definition of "there is something a `video_finished` condition could wait
 * for".
 *
 * The three tests are exactly the ones `WorkflowRunModel.resolvePlaybackMedia`
 * applies before it produces a `{ kind: "video" }` playback entry: a media
 * block, a non-empty url (a block with none resolves to no panel at all), and
 * an effective kind of video. They must agree, because one decides whether the
 * gate is armed and the other decides whether a `<video>` is ever rendered to
 * open it — a disagreement is a learner held on a step with no video on screen.
 * The harness drives both and asserts they answer alike.
 */
export function stepHasPlayableVideo(step: WorkflowStep): boolean {
  const media = step.media;
  if (media === null) return false;
  if (media.url.trim().length === 0) return false;
  return resolveMediaType(media) === "video";
}

/**
 * Parses an authored timecode to seconds: `12`, `1:05`, `0:01:05.5`. Null when
 * the text is not a timecode at all, which `validate()` reports as an error —
 * a start the player silently ignored would look like the trim was applied.
 */
export function parseMediaTimecodeSeconds(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;

  const parts = trimmed.split(":");
  if (parts.length > 3) return null;

  let seconds = 0;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    // Only the last (seconds) segment may be fractional.
    const pattern = i === parts.length - 1 ? /^\d+(?:\.\d+)?$/ : /^\d+$/;
    if (!pattern.test(part)) return null;
    seconds = seconds * 60 + Number(part);
  }

  return Number.isFinite(seconds) ? seconds : null;
}

/**
 * `m:ss` — the INVERSE of {@link parseMediaTimecodeSeconds}, and deliberately
 * beside it so the two forms of a media timecode have one home.
 *
 * Used by the media panel's readout and by `control_media`'s reply, so the
 * position the Guide says out loud is written the same way the learner sees
 * it. Clamped at zero and floored: a negative or fractional readout is never
 * useful, and a seek lands on a fraction routinely.
 */
export function formatMediaTimecode(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * One entry of the header's `knowledge.include` list: a document this workflow
 * draws on. PLACEHOLDER — the web has no document pipeline yet (the Asset
 * Library's Docs tab is an explicit "not available" state), so ids are carried
 * and validated for shape but nothing resolves them. It is machine-owned for
 * that reason: the authored view does not render it, because an author typing
 * ids by hand could only produce references nothing can resolve.
 */
export class WorkflowKnowledgeDoc {
  doc = "";
  /** Raw YAML value of an entry that is not a `{ doc: … }` mapping. */
  invalidRaw: unknown = undefined;

  get isInvalid(): boolean {
    return this.invalidRaw !== undefined;
  }
}

/**
 * One group — an optional higher-level bucket of steps. Groups carry no
 * behavior of their own: membership lives on each step's `groupName`, and a
 * group entry here fixes ordering and allows an EMPTY group to exist (its
 * heading renders after the steps).
 */
export class WorkflowGroup {
  name = "";
  /** Member steps in document order (same objects as `WorkflowDocument.steps`). */
  steps: WorkflowStep[] = [];
}

/** Runtime progress persisted per account/user/workflow in DynamoDB. */
export interface WorkflowRunState {
  readonly run: "in progress" | "not running";
  readonly current: string | null;
  readonly currentGroup: string | null;
  readonly currentIndex: number | null;
  readonly stepCount: number;
  readonly completed: readonly string[];
  /**
   * Every step the run has actually LANDED on, completed or not — the ground
   * the learner has covered. Kept apart from {@link completed} because the two
   * answer different questions: Next completes a step, while Back, a trigger
   * button and an agent jump move without completing anything. It is what the
   * Guide's named jumps are limited to, so a step that was skipped over (a
   * trigger jumping past it) is not somewhere the agent may send the learner.
   */
  readonly visited: readonly string[];
  /**
   * The learner's PATH: every arrival in order, duplicates included, ending on
   * the step they are on now. This is what "go back 3 steps" counts, so it is a
   * stack and not a set — a trigger can land them on the same step twice, and
   * both landings are moves they made.
   *
   * It REWINDS. Back, `direction=previous`, `back=N` and an outline pick all
   * drop the entries they returned from, so the stack always reads as the route
   * from the start to right now and going back twice cannot ping-pong between
   * two steps. {@link visited} is the monotonic half of the same story: it never
   * shrinks, which is why it — not this — is what gates where they may be sent.
   */
  readonly visitStack: readonly string[];
  /**
   * Where to CONTINUE from. Set to the step the learner was on when a rewind
   * took them back over covered ground (they were interrupted mid-flow), and to
   * the current step when a run stops. Cleared when the run arrives there again.
   */
  readonly resumePoint: string | null;
  /** Prose the guide agent acts on (part of the run-state prompt bullets). */
  readonly next: string;
}

/** Web stand-in for C# `KeyValuePair<string, string>` entries in the report lists. */
export interface WorkflowKeyValuePair {
  key: string;
  value: string;
}

/** Addressable field an issue points at. */
export type WorkflowIssueField =
  | "product"
  | "knowledge"
  | "scene"
  | "attachment"
  | "scripts"
  | "next_target";

/**
 * What an addressed issue is a fact ABOUT — which decides both its wording and
 * who can clear it.
 *
 * - `reference` — the PROJECT moved out from under the document: a renamed
 *   scene, a deleted model. Clears when the project matches again.
 * - `command` — the document disagrees with the tool REGISTRY: a tool name
 *   that does not exist, a command a script cannot run, an argument of the
 *   wrong type or a value outside its declared enum. Clears when the document
 *   is corrected.
 * - `structure` — the document breaks a rule of the FORMAT itself in a way
 *   that stops it playing: a `next_target` that cannot be followed. The only kind that is
 *   an ERROR rather than a warning — it is
 *   mirrored into {@link WorkflowValidationReport.errors}, so it is the play
 *   gate like every other error, and the panel shows it in red. It does NOT
 *   replace the queue or force raw mode the way an unparsable file does: the
 *   file loads and the editor can point at the row.
 *
 * Kept apart because {@link WorkflowValidationReport.hasBrokenReferences}
 * drives a prompt bullet that says "its scene or part names no longer match
 * the project" — folding a misspelled tool into that would make the agent say
 * something false.
 */
export type WorkflowIssueKind = "reference" | "command" | "structure";

/**
 * One issue the editor can anchor to a source row, also mirrored into
 * {@link WorkflowValidationReport.warnings} (or `errors`, for a `structure`
 * issue) so it reaches the agent through `get_workflow`'s report.
 *
 * These are FACTS, not suggestions — checkable against the project or the
 * registry — which is why the panel surfaces them where it deliberately does
 * not surface the rest of `validate`'s structural advice.
 */
export interface WorkflowAddressedIssue {
  readonly kind: WorkflowIssueKind;
  readonly step: string;
  readonly field: WorkflowIssueField;
  readonly title: string;
  readonly message: string;
  /**
   * Path into the stored YAML (`steps[i].scripts[j].steps[k].args…`) so the
   * raw editor can mark the source row, not only the enclosing step section.
   */
  readonly yamlPath: ReadonlyArray<string | number>;
}

/** Part-path index for one loaded scene. Paths are stored lowercase for display names. */
export interface WorkflowScenePartIndex {
  readonly displayPaths: ReadonlySet<string>;
  readonly indexPaths: ReadonlySet<string>;
}

/**
 * Project snapshot the validator uses to check scene/part references.
 * Built by the file model; omit it for structural checks only.
 *
 * A scene missing from {@link partsByScene} is not loaded, and part paths are
 * scene-relative, so a step pointing at it is left unchecked until
 * `onLoadSceneCompleted` brings its real tree in. Scene identity (name, 1-based
 * index, id) is always checkable from {@link scenes}.
 */
export interface WorkflowCatalogScene {
  readonly id: string;
  readonly name: string;
  /** 1-based list index, same numbering as `go_to_scene` / the scenes list. */
  readonly index: number;
  readonly loaded: boolean;
}

export interface WorkflowReferenceCatalog {
  readonly scenes: ReadonlyArray<WorkflowCatalogScene>;
  /** Lowercase scene id → part index. Missing key = scene not loaded. */
  readonly partsByScene: ReadonlyMap<string, WorkflowScenePartIndex>;
  /**
   * Status for the workflow header's `product` value. Built by the file model
   * from the canonical product row, not from product-document link rows.
   */
  readonly product?: WorkflowProductReference | null;
  /**
   * Canonical `knowledge.include[].doc` key statuses. Built by the file model
   * from canonical document rows, so validation can name missing direct
   * includes without reaching into storage.
   */
  readonly knowledgeDocs?: ReadonlyMap<string, WorkflowKnowledgeDocumentReference> | null;
}

export interface WorkflowProductReference {
  readonly productKey: string;
  readonly state: "found" | "missing" | "archived" | "unavailable";
  readonly displayName: string;
  readonly reason?: string;
}

export interface WorkflowKnowledgeDocumentReference {
  readonly documentNameKey: string;
  readonly state: "found" | "missing" | "archived" | "unavailable";
  readonly displayName: string;
  readonly reason?: string;
}

/**
 * Structural validation output for a workflow document. Errors are what makes
 * a workflow unplayable ({@link isWorkflowPlayable}); warnings and the
 * addressed-issue list are input for the workflow-builder role to resolve.
 * When a {@link WorkflowReferenceCatalog} is supplied, unresolved scene/part
 * references are WARNINGS, not errors — a stale reference is a thing to fix,
 * not a reason to refuse the run. Script-command issues are warnings for the
 * same reason, and for one more: the runtime is more tolerant than the
 * declared schemas, so refusing on one would refuse files that play.
 */
export class WorkflowValidationReport {
  errors: string[] = [];
  warnings: string[] = [];
  /** Step name -> authored Scene value, for the builder to resolve against the project. */
  sceneReferences: WorkflowKeyValuePair[] = [];
  /**
   * Addressed issues in document order (also appended to {@link warnings}, or
   * to {@link errors} for a `structure` issue).
   * Both kinds live in ONE list so the panel's stepper walks them the way the
   * document reads rather than in two blocks by category.
   */
  issues: WorkflowAddressedIssue[] = [];
  /** True when `validate` was given a catalog and checked live references. */
  checkedReferences = false;
  /**
   * True when the tool registry was available and script commands were checked
   * against it. False in a headless parse that never built the registry, where
   * claiming a pass would be claiming a check that did not run.
   */
  checkedCommands = false;

  get isStructurallyValid(): boolean {
    return this.errors.length === 0;
  }

  get hasBrokenReferences(): boolean {
    return this.issues.some((issue) => issue.kind === "reference");
  }

  /** True when a script names a tool that does not exist, or misuses one. */
  get hasCommandIssues(): boolean {
    return this.issues.some((issue) => issue.kind === "command");
  }

  toSummaryText(): string {
    const lines: string[] = [];
    lines.push(
      this.isStructurallyValid ? "Structural validation: PASSED" : "Structural validation: FAILED",
    );

    if (this.checkedReferences) {
      lines.push(
        this.hasBrokenReferences
          ? "Project references: FAILED (unresolved scene or part references)"
          : "Project references: PASSED",
      );
    }

    if (this.checkedCommands) {
      lines.push(
        this.hasCommandIssues
          ? "Script commands: FAILED (a step names a tool that does not exist, or misuses one)"
          : "Script commands: PASSED",
      );
    }

    if (this.errors.length > 0) {
      lines.push("Errors:");
      for (const error of this.errors) lines.push(`- ${error}`);
    }

    if (this.warnings.length > 0) {
      lines.push("Warnings:");
      for (const warning of this.warnings) lines.push(`- ${warning}`);
    }

    // Without a catalog the builder still needs the authored scene list to
    // resolve by hand. Once we have checked them, broken ones are warnings
    // and valid ones do not need listing.
    if (!this.checkedReferences && this.sceneReferences.length > 0) {
      lines.push("Scene references to resolve against the project:");
      for (const reference of this.sceneReferences) {
        lines.push(`- step "${reference.key}" -> scene "${reference.value}"`);
      }
    }

    return lines.join("\n").trimEnd();
  }
}

/**
 * The parsed workflow document: identity, header, groups, steps, run state.
 * Instances are treated as immutable snapshots by convention — edit paths
 * build a NEW document (clone + change) and save it through the file model,
 * so a consumer holding a document across an await never sees it mutate.
 */
/** A dock side, as the FORMAT spells it. */
export const WORKFLOW_PANEL_DOCK_SIDES = ["left", "right", "top", "bottom"] as const;
export type WorkflowPanelDockSide = (typeof WORKFLOW_PANEL_DOCK_SIDES)[number];

/** A viewport alignment, as the FORMAT spells it. */
export const WORKFLOW_PANEL_SCREEN_ALIGNS = ["start", "center", "end"] as const;
export type WorkflowPanelScreenAlign = (typeof WORKFLOW_PANEL_SCREEN_ALIGNS)[number];

/**
 * A dock's CROSS-AXIS alignment, as the FORMAT spells it.
 *
 * The same three words as the screen aligns above and deliberately its own
 * constant, for the reason the note below gives about the solver's types: they
 * answer different questions (this one aligns a child within its parent's face;
 * that one places a group in the viewport) and only one of them may be absent.
 * One list would make a change to either silently change the other.
 */
export const WORKFLOW_PANEL_DOCK_ALIGNS = ["start", "center", "end"] as const;
export type WorkflowPanelDockAlign = (typeof WORKFLOW_PANEL_DOCK_ALIGNS)[number];

/**
 * ⚠ These two vocabularies are the FORMAT's, deliberately re-declared here
 * rather than imported from `models/ui/magneticDockLayout.ts`.
 *
 * They happen to have the same members as that module's `DockSide` /
 * `ScreenAlign` today, and they must not be aliased to them: a stored file is a
 * wire format with a second reader and a long life, while those are a live
 * implementation detail of the magnetic solver. Aliasing would let a refactor
 * of the solver silently redefine what every stored layout MEANS. The same
 * reasoning `USER_INPUT_ALLOWANCES` already follows.
 */

/** Where one panel sits: on `side` of `parent`, aligned by `align`. */
export class WorkflowPanelDock {
  parent = "";
  /** Null while {@link invalidSide} holds something unrecognised. */
  side: WorkflowPanelDockSide | null = null;
  /** An unrecognised side, preserved verbatim so a round trip cannot lose it. */
  invalidSide = "";
  /**
   * Where the panel sits on the face's own axis: its near edge, its far edge,
   * or the middle.
   *
   * OPTIONAL, unlike `side`. Absent means "not stated", which the restore reads
   * as the alignment a dock had before this key existed — bottoms level for a
   * left/right dock, lefts aligned for a top/bottom one. That is a placement
   * written by an older build restoring where it was PUT rather than silently
   * re-centring; it is not a compatibility branch in the parser, which simply
   * has one more optional key.
   */
  align: WorkflowPanelDockAlign | null = null;
  /** An unrecognised alignment, preserved verbatim like {@link invalidSide}. */
  invalidAlign = "";
}

/** Where one panel's GROUP sits against the viewport. A null axis is free. */
export class WorkflowPanelScreenDock {
  x: WorkflowPanelScreenAlign | null = null;
  y: WorkflowPanelScreenAlign | null = null;
  invalidX = "";
  invalidY = "";
}

/**
 * One panel's stored placement — a DEVIATION from its designed arrangement.
 *
 * A panel sitting where the shell's own CSS and its declared designed dock /
 * screen dock / embed put it is OMITTED entirely, which is what makes
 * `panels_layout` "only present when the layout was modified" fall out
 * mechanically rather than needing a dirty flag. It is also the guard against
 * a stale default: the designed arrangement has moved more than once
 * (`--cf-wfp-right` was re-centred on 2026-08-25, `--cf-wfp-baseline` changed
 * the day after), and storing an absolute snapshot would pin every project
 * that ever had a panel nudged to whichever geometry was current that week.
 * Storing only overrides means an unmodified panel inherits a changed default
 * for free.
 *
 * The four placements are mutually exclusive in practice — a panel is embedded,
 * OR docked to another panel, OR a group root with a screen dock and/or a free
 * offset — but all four are carried per entry rather than as a tagged union,
 * because the registry itself holds them as independent fields and a lossy
 * mapping here would be a second source of truth about where a panel is.
 */
export class WorkflowPanelPlacement {
  /** The registry id this placement is for. */
  panel = "";
  dock: WorkflowPanelDock | null = null;
  screen: WorkflowPanelScreenDock | null = null;
  /**
   * Where the panel's top-left was, in viewport pixels. Null for a panel that a
   * dock places on its own.
   *
   * ⚠ An absolute POSITION, and it REPLACES the `free` key (2026-08-31), which
   * held a translation from the panel's CSS home as a fraction of the viewport.
   * That could not work and was the reported drift: several of these panels are
   * positioned by CSS that is itself viewport-relative (the floating nav row is
   * `right: calc(50% - w/2)`), so a translation FROM that home and the home
   * itself move by unrelated laws — scaling the translation proportionally is
   * scaling something that is not a proportion of anything. A layout captured
   * at 1280 wide and restored at 1920 put the nav row 513px from where it was
   * left, and a run entered from a phone rewrote it in the phone's fractions.
   *
   * A position needs no scaling: the restore subtracts the CURRENT home, which
   * is what the live resize path (`rebaseFreePlacements`) already does, so the
   * two are one rule. Out of reach on a smaller display is handled by the
   * solver's clamp, as it is for any oversized group.
   *
   * `free` is TOLERATED and ignored on read rather than refused — see
   * {@link WorkflowUiState}. A layout written by an older build therefore
   * restores its docks and screen docks and puts a freely-placed panel back on
   * its designed spot, where one drag re-saves it.
   */
  position: { x: number; y: number } | null = null;
  /** The zone this panel is embedded in, e.g. `workflow-play:bottom`. */
  embed: string | null = null;

  get isEmpty(): boolean {
    return (
      this.panel.trim().length === 0 &&
      this.dock === null &&
      this.screen === null &&
      this.position === null &&
      this.embed === null
    );
  }
}

/**
 * `workflow_ui` — presentation state the app writes, not the author.
 *
 * Machine-owned and INTERNAL: it has no line in the authored view, no patch
 * path, and the agent is refused by name if it tries to write one. Optional in
 * every sense — absent is the normal state, and a workflow only grows the
 * section once somebody has moved a panel.
 *
 * ⚠ WEB-ONLY, and it is a deliberate FORK. The desktop/Quest client parses this
 * same file and its top-level allow-list
 * (`WorkflowYamlSerializer.cs:32`) does not carry `workflow_ui` — an unknown
 * top-level key there makes `Parse` return a NULL document, so a project whose
 * file has grown this section no longer opens on desktop. That was accepted as
 * a direction call rather than overlooked. Note the clients had ALREADY
 * diverged in the other direction and nobody had recorded it: Unity still
 * accepts `status` and `reviewed_hash`, which the web removed (2026-08-20 and
 * 2026-08-13), so a Unity-authored file is already unreadable on web today.
 */
export class WorkflowUiState {
  /** Per-panel overrides. Empty means the section serializes to nothing. */
  panelsLayout: WorkflowPanelPlacement[] = [];

  get isEmpty(): boolean {
    return this.panelsLayout.length === 0;
  }
}

export class WorkflowDocument {
  /** Stable workflow id, minted by the file model on create/import. */
  uid = "";
  /** Workflow kind — schema + execution behaviour ({@link WORKFLOW_TYPES}). */
  type = "";
  /** Format version this file was written against ({@link WORKFLOW_FORMAT_VERSION}). */
  version = "";
  /** Bound project id (identity; the file model stamps it). */
  projectId = "";
  /** Bound project display name at binding time (informational). */
  projectName = "";
  workflowName = "";
  description: string | null = null;
  expectedDuration: string | null = null;
  /** Default speech language for the guide (BCP-47-ish code, e.g. `en`). */
  language: string | null = null;
  /** What the workflow is about — the product / equipment it covers. */
  product: string | null = null;
  /** `knowledge.include` — documents this workflow draws on (placeholder). */
  knowledgeDocs: WorkflowKnowledgeDoc[] = [];
  /**
   * What the learner may reach during a run — see {@link USER_INPUT_ALLOWANCES}.
   * Absent or empty = NOTHING: a guided procedure is the constrained case, and
   * a default of "everything is available" would make every workflow that
   * wants a focused run say so explicitly.
   */
  userInputAllowed: WorkflowUserInputAllowance[] | null = null;
  /** Unrecognised members, preserved and reported — see the step's copy. */
  invalidUserInputAllowed: string[] = [];
  /**
   * Start the run automatically when the project is opened on a DESKTOP
   * browser, if the workflow is playable. Absent = false, matching
   * {@link userInputAllowed}: auto-playing is the surprising behaviour, so a
   * workflow that wants it says so.
   */
  autostartDesktop: boolean | null = null;
  /**
   * The same for a HEADSET session. Split from the desktop flag rather than
   * one `autostart` because the two contexts are genuinely different products:
   * a desktop author opens a project to EDIT it, while entering a project on a
   * headset is already a viewing act (project entry itself enters XR), so a
   * workflow can reasonably auto-play in one and not the other.
   */
  autostartHeadset: boolean | null = null;
  /**
   * Whether a new run picks up where the last one left off. Absent = FALSE:
   * every Play starts the learner at the first step with nothing completed and
   * nothing seen, because a training workflow is normally taken from the top,
   * and silently resuming into the middle of one is the surprising behaviour.
   * A workflow that is genuinely long enough to leave and come back to says so
   * ({@link isProgressRemembered}).
   *
   * It gates the READ of `workflow_state`, not the write: the run always
   * publishes where it is, so the panel and the agent see the live position
   * either way.
   */
  rememberProgress: boolean | null = null;
  /**
   * Whether a HEADSET run puts the stage down in the wearer's room before it
   * plays. Absent = false.
   *
   * True means the run opens console placement first and waits: the workflow
   * only makes sense with the table sitting somewhere particular in the real
   * room, so being asked where is part of starting it. See
   * {@link isConsolePlacementRequired}.
   *
   * Headset-only by nature rather than by a second flag — console placement is
   * an XR mode and there is nothing on a desktop for it to move — so a desktop
   * run ignores it and starts straight away, the way `autostart_headset` is
   * ignored on a desktop.
   */
  requiresConsolePlacement: boolean | null = null;
  /**
   * Whether the run's floating panels are FROZEN where the workflow puts them.
   * Absent = false.
   *
   * True means nobody rearranges them — not the learner, and not the author
   * either. That is the whole point of it and the one thing that makes it
   * different from the viewer lock it shares a mechanism with: a workflow whose
   * arrangement is part of how it teaches wants that arrangement to survive
   * whoever opens it, including the person building it.
   *
   * It freezes the arrangement rather than DEFINING one — where the panels sit
   * is still {@link WorkflowUiState.panelsLayout}, or the designed layout when
   * that is absent. So an author arranges first and locks second; locking a
   * workflow that has never been arranged simply pins the designed layout.
   *
   * Naturally run-scoped with no flag for it: the magnetic panels are rendered
   * only by a playing run (the editor's own outline popover is a separate,
   * non-magnetic element), so there is nothing outside one for this to reach.
   * See {@link isUiLocked}.
   */
  lockUi: boolean | null = null;
  /**
   * Whether this workflow defines its OWN panel arrangement. Absent = false.
   *
   * False (the normal case) means the run's floating panels are arranged by
   * whoever is looking at them, and their arrangement is remembered in that
   * BROWSER — one arrangement shared by every workflow that does not define
   * one, rather than a property of this document. {@link WorkflowUiState} is
   * ignored entirely while it is false, and nothing a person drags reaches this
   * file.
   *
   * True hands the arrangement to the workflow: `workflow_ui.panels_layout`
   * becomes what a run restores, only somebody who can edit the project may
   * change it, and a learner cannot move a panel at all.
   *
   * ⚠ The order is FLAG FIRST, then arrange. An author who drags the panels
   * with this off is writing to their own browser, and turning it on afterwards
   * starts from the designed layout — their earlier arrangement is not adopted,
   * because it was never this workflow's.
   *
   * Two capabilities are withdrawn while it is false, both because there is
   * nobody to withdraw them FROM: the viewer lock (the arrangement is the
   * viewer's own, so there is nothing of the author's to protect) and EMBEDDING
   * a panel inside another (a nested arrangement is a composition the workflow
   * makes, not a placement a passer-by chooses). See {@link isCustomUiEnabled}.
   */
  useCustomUi: boolean | null = null;
  /**
   * On a PHONE during a run, the Spark composer is for voice and nothing else.
   * Absent = false.
   *
   * A phone run arms the microphone with a tap on that composer, and the second
   * tap ordinarily stops listening and lets the keyboard up so the learner can
   * type instead. True takes the keyboard out of that cycle entirely: one tap
   * listens, the next stops, and the field never focuses.
   *
   * Set it for a workflow whose learner cannot usefully type — gloved hands, a
   * phone on a stand, a shop floor — where a keyboard covering half the screen
   * is a surface to dismiss rather than one to use.
   *
   * ⚠ PHONE-only and RUN-only, and not because it was scoped that way for
   * safety: there is no tap cycle to change anywhere else. On a desktop the
   * composer focuses on click like any other field, and outside a run the phone
   * panel listens the whole time it is open. See {@link isMobileKeyboardDisabled}.
   */
  disableMobileKeyboard: boolean | null = null;
  /**
   * `workflow_ui` — app-written presentation state. Null when absent, which is
   * the normal case; see {@link WorkflowUiState}.
   */
  workflowUi: WorkflowUiState | null = null;
  /** Groups in authored order (may include empty groups). */
  groups: WorkflowGroup[] = [];
  /** Steps in authored order — this IS the play order. */
  steps: WorkflowStep[] = [];

  findStep(stepName: string): WorkflowStep | null {
    if (isNullOrWhiteSpace(stepName)) return null;
    return this.steps.find((step) => equalsIgnoreCase(step.name, stepName)) ?? null;
  }

  findGroup(groupName: string): WorkflowGroup | null {
    if (isNullOrWhiteSpace(groupName)) return null;
    return this.groups.find((group) => equalsIgnoreCase(group.name, groupName)) ?? null;
  }

  findStepById(stepId: string): WorkflowStep | null {
    if (isNullOrWhiteSpace(stepId)) return null;
    return this.steps.find((step) => step.stepId === stepId.trim()) ?? null;
  }
}

/**
 * Resolves a navigation target name (a trigger's `go_to`) to the step it
 * lands on: a step match wins outright; a group name resolves to the group's
 * first step. Null when the name matches neither, or the group is empty.
 * The step-first precedence is the documented tie-break for a name that is
 * both — `validate()` warns on that collision.
 */
export function findNavigationTargetStep(
  document: WorkflowDocument | null,
  targetName: string,
): WorkflowStep | null {
  if (document === null || isNullOrWhiteSpace(targetName)) return null;

  const step = document.findStep(targetName);
  if (step !== null) return step;

  return document.findGroup(targetName)?.steps[0] ?? null;
}

// ---------------------------------------------------------------------------
// Playback scope — what a RUN can see
// ---------------------------------------------------------------------------
//
// A step carrying `disabled: true` stays in the document: it parses, it
// serializes, it renders in the authoring view, and its own content is still
// validated. It is only invisible to PLAYBACK. These four lookups are that
// boundary, and every question the run asks goes through one of them rather
// than through `document.steps` / `findStep` / `findNavigationTargetStep`,
// which stay DOCUMENT-scoped so the editor and the validator can still address
// a parked step in order to show and fix it.

/**
 * Whether this step is parked out of the run. Absent = false.
 *
 * The one lookup, so the sequence, the counting, the outline, the trigger
 * buttons and the agent's jumps cannot come to different answers about whether
 * a step exists for the learner.
 */
export function isStepDisabled(step: WorkflowStep): boolean {
  return step.disabled ?? false;
}

/**
 * The steps a run can actually reach, in play order — `document.steps` minus
 * the parked ones. This is what "how many steps" and "what is next" mean at
 * playback; a run must never index into `document.steps` directly, or a parked
 * step shows up as a position the learner is counted against and a Next that
 * lands nowhere.
 */
export function getPlayableSteps(document: WorkflowDocument | null): WorkflowStep[] {
  if (document === null) return [];
  return document.steps.filter((step) => !isStepDisabled(step));
}

/** The playable members of a group, in play order (the "n of m" scope). */
export function getPlayableGroupSteps(group: WorkflowGroup | null): WorkflowStep[] {
  if (group === null) return [];
  return group.steps.filter((step) => !isStepDisabled(step));
}

/**
 * A step by name, for the RUN: a parked step answers null, exactly as a step
 * that is not in the file at all does. That is what makes every refusal the
 * navigation paths already have — a jump to a step the document no longer
 * knows, a persisted position naming a deleted step — cover a parked one too,
 * with no second branch to keep in step.
 */
export function findPlayableStep(
  document: WorkflowDocument | null,
  stepName: string,
): WorkflowStep | null {
  const step = document?.findStep(stepName) ?? null;
  return step !== null && !isStepDisabled(step) ? step : null;
}

/**
 * A navigation target (a trigger's `go_to`) for the RUN. Same precedence as
 * {@link findNavigationTargetStep} — a step match wins, a group resolves to its
 * first member — except that parked steps are skipped on both sides, so a group
 * whose opening steps are all parked lands on its first LIVE one rather than on
 * a step nobody can reach.
 */
export function findPlayableNavigationTarget(
  document: WorkflowDocument | null,
  targetName: string,
): WorkflowStep | null {
  if (document === null || isNullOrWhiteSpace(targetName)) return null;

  const step = document.findStep(targetName);
  if (step !== null) return isStepDisabled(step) ? null : step;

  return getPlayableGroupSteps(document.findGroup(targetName))[0] ?? null;
}

/**
 * A `next_target` value as the model stores it: trimmed, and EMPTY IS ABSENT.
 * Unlike a label, an empty target has no meaning of its own ("the next step in
 * the file" is what absent already says), and treating the two alike is what
 * keeps the authored view's empty `Next goes to:` line from writing
 * `next_target: ""` into the file on every WYSIWYG save.
 */
export function normalizeNextTarget(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Where Next on `step` goes, for the RUN — the one answer the learner's Next
 * button, the agent's `direction=next`, the step-move check, the play panel's
 * `hasNext` and the Guide's run-state note all read, so none of them can
 * disagree about one press.
 *
 * 1. `next_target` set and it resolves to a playable step → that step (a group
 *    lands on its first live member, as a trigger's `go_to` does).
 * 2. Otherwise → the next playable step in the file.
 * 3. None → null: the run ends here, as it always has on the last step.
 *
 * A target that is set but does not resolve (unknown, disabled, itself) is a
 * validation ERROR, so it cannot reach a run that started from a valid file;
 * only an edit made mid-run can produce one, and then Next falls back to the
 * file order rather than doing nothing. A step reaching itself is refused here
 * too, belt to that error, so Next can never be a no-op move.
 *
 * Whether Next ENDS the run on this step (`is_completion_step`) is the caller's
 * question, asked before this one — an ending carries no target by rule.
 */
export function resolveNextStep(
  document: WorkflowDocument | null,
  step: WorkflowStep,
): WorkflowStep | null {
  if (document === null) return null;

  const targetName = normalizeNextTarget(step.nextTarget);
  if (targetName !== null) {
    const target = findPlayableNavigationTarget(document, targetName);
    if (target !== null && target !== step) return target;
  }

  const steps = getPlayableSteps(document);
  const index = steps.indexOf(step);
  return index >= 0 && index + 1 < steps.length ? steps[index + 1] : null;
}

/**
 * Rebuilds {@link WorkflowDocument.groups} membership from the steps'
 * `groupName` values, keeping existing group entries and appending entries
 * for group names only the steps mention. Call after any edit that changes
 * step membership or order.
 */
export function rebuildGroupMembership(document: WorkflowDocument): void {
  const byName = new Map<string, WorkflowGroup>();
  for (const group of document.groups) {
    group.steps = [];
    byName.set(group.name.toLowerCase(), group);
  }

  for (const step of document.steps) {
    if (step.groupName.length === 0) continue;
    let group = byName.get(step.groupName.toLowerCase());
    if (!group) {
      group = new WorkflowGroup();
      group.name = step.groupName;
      document.groups.push(group);
      byName.set(group.name.toLowerCase(), group);
    }

    // Canonicalize the step's spelling to the group entry's.
    step.groupName = group.name;
    group.steps.push(step);
  }
}

// ---------------------------------------------------------------------------
// Display-field semantics (single source for the Workflow panel + validation)
// ---------------------------------------------------------------------------

/**
 * A navigation button's authored label, trimmed and unquoted, or null when the
 * author wrote none. What that null MEANS is the caller's — the two buttons
 * read it differently on purpose; see the two getters below.
 */
function buttonLabelOf(value: string | null): string | null {
  const label = trimChars((value ?? "").trim(), "\"'").trim();
  return label.length === 0 ? null : label;
}

/**
 * The Next button's label, or null when the step has NO Next button.
 *
 * **Here the label IS the switch.** A written label shows the button and titles
 * it; empty or absent hides it. There is no `shown` / `hidden` keyword and no
 * default label to fall back on — the same rule `step_instructions` follows, so
 * the author sets one thing per button instead of a mode and a caption that can
 * disagree. (`hidden` typed into the field is therefore just a button captioned
 * "hidden"; the field is free text.)
 *
 * Hiding Next is a real authoring act: a step with no Next forces the learner
 * to pick a trigger, which is how a branch point is written — and the dead-end
 * check keys off exactly this null.
 */
export function getStepNextButtonLabel(step: WorkflowStep): string | null {
  return buttonLabelOf(step.nextButton);
}

/**
 * Whether the author wrote any trigger button on this step (a well-formed entry
 * with a label) — which makes it a QUESTION; with no Next label, its buttons
 * are the only way on. Counts what was WRITTEN, not what a run renders: a
 * button hidden because its target is parked is still authored branching. The
 * one predicate the validator's dead-end warning and the run model's Next
 * refusal share, so the file check and the runtime guard cannot disagree about
 * which steps are questions.
 */
export function stepHasTriggerButtons(step: WorkflowStep): boolean {
  return step.triggers.some((trigger) => !trigger.isInvalid && !isNullOrWhiteSpace(trigger.label));
}

/**
 * The Back button's label, or null when the author wrote none — which means an
 * UNLABELLED button, never a hidden one.
 *
 * **Back deliberately does NOT follow Next's label-is-the-switch rule**, and
 * the asymmetry is the point rather than an oversight (user direction,
 * 2026-08-27). Whether the learner can go back is a fact about the PATH they
 * walked, which only the run model knows and which changes under an author's
 * feet — so it cannot be an authored field. Whether they can go FORWARD from a
 * step is a decision the author makes and the validator checks. One is derived,
 * the other is authored, and only an authored one can have a switch.
 *
 * So the panel renders Back on `hasPrevious` and titles it from this: a label
 * gives it words, and null draws the arrow alone. There is no way to write a
 * step that hides Back while the learner still has somewhere to go back to.
 */
export function getStepBackButtonLabel(step: WorkflowStep): string | null {
  return buttonLabelOf(step.backButton);
}

/**
 * Whether pressing Next on this step ENDS the run rather than advancing.
 * Absent = false.
 *
 * The one lookup, so the learner's Next button, the agent's `direction=next`
 * and the play panel's own wording cannot come to different answers about what
 * the same press does.
 */
export function isCompletionStep(step: WorkflowStep): boolean {
  return step.isCompletionStep ?? false;
}

/**
 * What the learner may reach while this step plays: the step's own list when it
 * has one, else the workflow's, else nothing. A step overriding the header is
 * the point of having both — a mostly-guided procedure can still open up for
 * one exploratory step, and a mostly-open one can close a step down with an
 * empty list.
 *
 * `all` is expanded HERE — the one place — so no consumer downstream has to
 * know the wildcard exists, and adding a kind to {@link USER_INPUT_KINDS}
 * widens every list that already says `all` with no stored file changing.
 *
 * Returns a fresh array so a caller cannot mutate the document through it.
 */
export function resolveUserInputAllowed(
  document: WorkflowDocument,
  step: WorkflowStep | null,
): WorkflowUserInputKind[] {
  const stored = step?.userInputAllowed ?? document.userInputAllowed ?? [];
  if (stored.includes(USER_INPUT_ALL)) return [...USER_INPUT_KINDS];
  return stored.filter((entry): entry is WorkflowUserInputKind => entry !== USER_INPUT_ALL);
}

/**
 * Whether `kind` is open on this step. Goes through the resolver rather than
 * testing the stored list, so the wildcard cannot be expanded two ways.
 */
export function isUserInputKindAllowed(
  document: WorkflowDocument,
  step: WorkflowStep | null,
  kind: WorkflowUserInputKind,
): boolean {
  return resolveUserInputAllowed(document, step).includes(kind);
}

/**
 * The step's INTERNAL one-line summary, trimmed. Never rendered to the learner
 * — {@link getStepInstructions} is the play panel's copy. This is the text the
 * guide matches a described step against, so it reaches the model through
 * `get_workflow` and nothing else.
 */
export function getStepContext(step: WorkflowStep): string {
  return (step.stepContext ?? "").trim();
}

/**
 * The step's play-panel copy, trimmed. Empty means the panel shows nothing
 * under the step name — emptiness IS the "hidden" state, which is why there is
 * no separate display toggle for it (unlike Back / Next, whose buttons have no
 * content of their own to be empty).
 */
export function getStepInstructions(step: WorkflowStep): string {
  return (step.stepInstructions ?? "").trim();
}

/**
 * The `say:` value that speaks the owning step's `step_instructions` instead of
 * a line of its own.
 *
 * A TOKEN rather than a second script-step key (`say_instructions:`, a `source:`
 * sibling) because it changes nothing about the file's shape: a say step is
 * still `{ say: <string> }`, every existing document still loads, and the
 * round-trip, the patch schema and the editor's say row are all untouched. The
 * angle brackets are what keep it unambiguous — no learner-facing line is
 * spelled that way, so a step that means the literal text cannot be captured by
 * accident.
 *
 * Resolved at PLAYBACK ({@link resolveSayText}), never at parse: the author can
 * edit the instructions after the script is written, and a value baked in at
 * parse time would be the stale copy this token exists to prevent.
 */
export const SAY_STEP_INSTRUCTIONS_TOKEN = "<step_instructions>";

/** Whether a `say:` line defers to the step's instructions rather than carrying its own text. */
export function isSayStepInstructionsToken(sayText: string): boolean {
  return equalsIgnoreCase(sayText.trim(), SAY_STEP_INSTRUCTIONS_TOKEN);
}

/**
 * What a `say:` line actually speaks: the step's instructions when it is the
 * token, else its own text. Empty when the token names instructions the step
 * does not have — the caller SKIPS the line rather than speaking nothing, since
 * an empty `INTERNAL SAY:` would leave the model to improvise a line the author
 * never wrote.
 *
 * The instructions are MARKDOWN (2026-08-27), and this returns them WITH their
 * markup — flattening is the caller's, at the speak boundary
 * (`WorkflowScriptRunner` -> `markdownToSpeechText`). Two jobs, kept apart: this
 * one answers "which text does the line carry", and the runner answers "what is
 * it safe to hand a model that has been told to speak it verbatim".
 *
 * ⚠ Until 2026-08-31 the markup was passed all the way through to the model,
 * on the reasoning that the recipient is a language model rather than a TTS
 * engine and would read `**straight**` as emphasis. That was wrong, and it was
 * wrong because of our own prompt: `base.md`'s Internal Speech Messages section
 * says to speak the line *verbatim, including punctuation*, so a model obeying
 * it has been told the asterisks ARE the line — and it sometimes read them out
 * ("asterix, asterix, part"). Do not restore the pass-through, and do not
 * "solve" it with a prompt rule instead: `markdownSpeech.ts` carries the whole
 * argument for why the flatten is what keeps `verbatim` absolute.
 */
export function resolveSayText(sayText: string, step: WorkflowStep | null): string {
  if (!isSayStepInstructionsToken(sayText)) return sayText;
  return step === null ? "" : getStepInstructions(step);
}

/**
 * Whether arriving at this step hands the SPOKEN turn to its own on-start
 * script — i.e. whether that script has a line to say.
 *
 * The narrow question, and the narrowness is the point: `m_aiInputHold` is
 * armed for ANY on-start script, but a script made only of tool steps
 * (`isolate`, `highlight_part`) speaks nothing, and telling the agent a script
 * is speaking there would be false. So the test is a say step whose text
 * RESOLVES to something — a `<step_instructions>` token on a step with no
 * instructions is a line the runner skips, which `validate()` already warns
 * about.
 *
 * Read by {@link WorkflowExecutor} after a `go_to_workflow_step` arrival, to
 * tell the model whether the author's own narration is playing
 * (`narration=script`) or nothing is said on the step (`narration=none`). Both
 * end the model's turn (2026-09-28).
 */
export function stepOnStartScriptSpeaks(step: WorkflowStep): boolean {
  const script = step.findScriptByTrigger("on_start");
  if (!script) return false;
  return script.steps.some(
    (scriptStep) =>
      scriptStep.isSay && resolveSayText(scriptStep.sayText, step).trim().length > 0,
  );
}

/**
 * Whether the document is playable: it validates clean. There is no authored
 * "ready" flag any more — a workflow that is structurally valid plays, and one
 * that is not says why through the SAME error list the panel's issue queue and
 * the builder's report already show. Broken scene/part references are warnings
 * (see {@link WorkflowValidationReport}), so a workflow whose project moved
 * under it still plays.
 *
 * ONE definition, shared: `WorkflowRunModel.tryStart` refuses on it and the
 * panel's Preview button disables on it, so the button and the run cannot
 * disagree about whether pressing Play would do anything.
 */
export function isWorkflowPlayable(
  document: WorkflowDocument | null,
  report: WorkflowValidationReport,
): boolean {
  // PLAYABLE steps, not `steps.length`: a file whose every step is parked has
  // entries but nothing to play. `validate` errors on that case too, so this
  // term is normally redundant — it is kept because the report is passed in and
  // may have been computed from a different document than the one being asked
  // about, and answering "playable" for a workflow with no live step is the one
  // wrong answer that ends with a Play button that does nothing.
  return document !== null && getPlayableSteps(document).length > 0 && report.isStructurallyValid;
}

/**
 * Whether opening the project should start the run by itself, for the
 * presentation context the client is in. Absent = false on both flags.
 *
 * Only answers "did the author ask for it" — whether the workflow can actually
 * play is a SEPARATE question, answered by {@link isWorkflowPlayable}, which
 * `WorkflowRunModel.tryStart` applies on its own.
 */
export function isAutostartRequested(
  document: WorkflowDocument,
  isHeadsetPresentation: boolean,
): boolean {
  return (isHeadsetPresentation ? document.autostartHeadset : document.autostartDesktop) ?? false;
}

/**
 * Whether a starting run restores the persisted `workflow_state` instead of
 * starting the learner over. Absent = false.
 *
 * Only the READ is gated. The run model keeps writing the state while it plays
 * (the panel and the agent read the live position from it), so this decides one
 * thing: whether `tryStart` inherits the previous run's current step, completed
 * list and covered ground, or drops all three.
 */
export function isProgressRemembered(document: WorkflowDocument): boolean {
  return document.rememberProgress ?? false;
}

/**
 * Whether starting the run should ask the wearer to place the console first.
 * Absent = false.
 *
 * Only answers "did the author ask for it". Whether placement can actually
 * happen — an XR session is live, so the mode has a controller behind it and a
 * way to end — is a SEPARATE question the run model asks its injected gate, and
 * a client that cannot place simply starts. Splitting the two the way
 * `isAutostartRequested` splits from `isWorkflowPlayable` keeps this pure and
 * keeps the availability rule in the one place that can see a session.
 */
export function isConsolePlacementRequired(document: WorkflowDocument): boolean {
  return document.requiresConsolePlacement ?? false;
}

/**
 * Whether the run's floating panels are frozen where the workflow puts them.
 * Absent = false.
 *
 * ⚠ This one applies to EVERYONE, author included — unlike every other gate in
 * this area, which asks who the user is. It is the author's own statement that
 * the arrangement is part of the workflow, so honouring it for them and not for
 * a learner would make it a preference rather than a lock. An author who wants
 * to rearrange turns it off, in the editor, where the switch is visible; a
 * learner has no such escape and is not meant to.
 *
 * Only answers "did the author ask for it". WHO is refused a gesture is the
 * panel layer's question — `useWorkflowPanelLayout` ORs this with its viewer
 * term and hands the result to the magnetic registry — the same split
 * {@link isConsolePlacementRequired} has from the run model's availability
 * gate, and {@link isAutostartRequested} from {@link isWorkflowPlayable}.
 */
export function isUiLocked(document: WorkflowDocument): boolean {
  return document.lockUi ?? false;
}

/**
 * Does this workflow take the keyboard out of the phone composer's tap cycle?
 * Absent = false. See {@link WorkflowDocument.disableMobileKeyboard}.
 */
export function isMobileKeyboardDisabled(document: WorkflowDocument): boolean {
  return document.disableMobileKeyboard ?? false;
}

/**
 * Does this workflow define its own panel arrangement? `use_custom_ui`, absent
 * = false. See {@link WorkflowDocument.useCustomUi}.
 *
 * The ONE lookup, so no consumer re-derives it — the convention
 * {@link isUiLocked} and {@link resolveUserInputAllowed} already follow. It
 * answers only "did the author claim the arrangement". WHERE the panels then
 * come from, and who may move them, is the panel layer's question:
 * `useWorkflowPanelLayout` reads this to choose between the document's
 * `workflow_ui` and the browser's own remembered arrangement.
 */
export function isCustomUiEnabled(document: WorkflowDocument): boolean {
  return document.useCustomUi ?? false;
}

// ---------------------------------------------------------------------------
// Deep clone (edit paths build a new document rather than mutating a shared one)
// ---------------------------------------------------------------------------

/**
 * The stored panel overrides, or an empty list.
 *
 * ⚠ Only meaningful while {@link isCustomUiEnabled} — a workflow that does not
 * claim its arrangement never has one restored from here, whatever the section
 * holds. This is the raw lookup, and the gate is the caller's.
 *
 * ONE lookup, so no consumer re-derives it — the convention
 * {@link resolveUserInputAllowed} and {@link resolveStepCompletionCondition}
 * already follow. It FAILS OPEN by design: an entry naming a panel this shell
 * does not have is not filtered here, because which panels exist is a property
 * of the shell (desktop / phone / XR each render a different set) and the
 * headless model has no way to know. The restore is what skips an id it cannot
 * place, and skipping means that panel keeps its designed placement rather than
 * the file being refused.
 */
export function resolvePanelsLayout(
  document: WorkflowDocument,
): readonly WorkflowPanelPlacement[] {
  return document.workflowUi?.panelsLayout ?? [];
}

/** Deep-copies `workflow_ui`. See the ⚠ in {@link cloneWorkflowDocument}. */
function cloneWorkflowUi(source: WorkflowUiState | null): WorkflowUiState | null {
  if (source === null) return null;
  const clone = new WorkflowUiState();
  clone.panelsLayout = source.panelsLayout.map((entry) => {
    const placement = new WorkflowPanelPlacement();
    placement.panel = entry.panel;
    if (entry.dock !== null) {
      const dock = new WorkflowPanelDock();
      dock.parent = entry.dock.parent;
      dock.side = entry.dock.side;
      dock.invalidSide = entry.dock.invalidSide;
      // ⚠ A manual field list with no spread, so a field added to
      // `WorkflowPanelDock` and not added HERE is stripped silently — from
      // every agent patch on one side and every WYSIWYG save on the other.
      dock.align = entry.dock.align;
      dock.invalidAlign = entry.dock.invalidAlign;
      placement.dock = dock;
    }
    if (entry.screen !== null) {
      const screen = new WorkflowPanelScreenDock();
      screen.x = entry.screen.x;
      screen.y = entry.screen.y;
      screen.invalidX = entry.screen.invalidX;
      screen.invalidY = entry.screen.invalidY;
      placement.screen = screen;
    }
    placement.position =
      entry.position === null ? null : { x: entry.position.x, y: entry.position.y };
    placement.embed = entry.embed;
    return placement;
  });
  return clone;
}


/**
 * `workflow_ui.panels_layout` — WARNINGS only, never errors.
 *
 * A stored layout is cosmetic and app-written: nothing in it can make a
 * workflow unplayable, so nothing in it may refuse the file. What is worth
 * reporting is the case where the section says something that cannot be acted
 * on, because that is invisible otherwise — the restore just silently skips it
 * and the author sees a panel back on its designed placement with no
 * explanation.
 *
 * Note what is deliberately NOT checked here: whether a panel id or a zone id
 * actually EXISTS. Which panels exist is a property of the shell — desktop,
 * phone and XR each render a different set, and a step withholding `ai_input`
 * removes one mid-run — so the headless model has no way to know, and a
 * warning derived from the wrong shell would be worse than none. The restore
 * skips an id it cannot place, which leaves that panel on its designed spot.
 */
function checkPanelsLayout(
  document: WorkflowDocument,
  report: WorkflowValidationReport,
): void {
  const layout = resolvePanelsLayout(document);
  if (layout.length === 0) return;

  const seen = new Set<string>();
  for (const entry of layout) {
    const key = entry.panel.toLowerCase();
    if (seen.has(key)) {
      report.warnings.push(
        `Panel "${entry.panel}" appears more than once in workflow_ui.panels_layout; ` +
          "only the first placement is restored.",
      );
    }
    seen.add(key);

    if (entry.dock !== null && entry.dock.invalidSide.length > 0) {
      report.warnings.push(
        `Panel "${entry.panel}" has an unknown dock side "${entry.dock.invalidSide}" ` +
          `(expected ${WORKFLOW_PANEL_DOCK_SIDES.join(", ")}); its dock is ignored.`,
      );
    }
    if (entry.dock !== null && entry.dock.invalidAlign.length > 0) {
      // Narrower than the side's: an unreadable ALIGN costs the dock its
      // cross-axis placement, not the dock itself, so the panel still lands on
      // the right face.
      report.warnings.push(
        `Panel "${entry.panel}" has an unknown dock alignment "${entry.dock.invalidAlign}" ` +
          `(expected ${WORKFLOW_PANEL_DOCK_ALIGNS.join(", ")}); its default alignment is used.`,
      );
    }
    if (entry.dock !== null && entry.dock.parent.trim().length === 0) {
      report.warnings.push(
        `Panel "${entry.panel}" has a dock with no parent; its dock is ignored.`,
      );
    }
    for (const [axis, bad] of [
      ["x", entry.screen?.invalidX ?? ""],
      ["y", entry.screen?.invalidY ?? ""],
    ] as const) {
      if (bad.length > 0) {
        report.warnings.push(
          `Panel "${entry.panel}" has an unknown screen ${axis} alignment "${bad}" ` +
            `(expected ${WORKFLOW_PANEL_SCREEN_ALIGNS.join(", ")}); that axis is ignored.`,
        );
      }
    }

    const placed =
      entry.dock !== null ||
      entry.screen !== null ||
      entry.position !== null ||
      entry.embed !== null;
    if (!placed) {
      report.warnings.push(
        `Panel "${entry.panel}" is listed in workflow_ui.panels_layout with no placement; ` +
          "the entry does nothing.",
      );
    }
  }
}

export function cloneWorkflowDocument(source: WorkflowDocument): WorkflowDocument {
  const document = new WorkflowDocument();
  document.uid = source.uid;
  document.type = source.type;
  document.version = source.version;
  document.language = source.language;
  document.product = source.product;
  document.userInputAllowed =
    source.userInputAllowed === null ? null : [...source.userInputAllowed];
  document.invalidUserInputAllowed = [...source.invalidUserInputAllowed];
  document.autostartDesktop = source.autostartDesktop;
  document.autostartHeadset = source.autostartHeadset;
  document.rememberProgress = source.rememberProgress;
  document.requiresConsolePlacement = source.requiresConsolePlacement;
  document.lockUi = source.lockUi;
  document.disableMobileKeyboard = source.disableMobileKeyboard;
  document.useCustomUi = source.useCustomUi;
  document.knowledgeDocs = source.knowledgeDocs.map((entry) => {
    const clone = new WorkflowKnowledgeDoc();
    clone.doc = entry.doc;
    clone.invalidRaw = entry.invalidRaw;
    return clone;
  });
  /*
    ⚠ `cloneWorkflowDocument` is a MANUAL field list with no spread, and
    `applyWorkflowPatch` starts from this clone and then serializes it — so a
    field omitted here is silently STRIPPED from the file on every agent patch,
    and because the `before` text was serialized from the original the patch
    would also report `changed: true` and re-upload the stripped file. Nothing
    on the format checklist covers this line. Add every new document field.
  */
  document.workflowUi = cloneWorkflowUi(source.workflowUi);
  document.projectId = source.projectId;
  document.projectName = source.projectName;
  document.workflowName = source.workflowName;
  document.description = source.description;
  document.expectedDuration = source.expectedDuration;
  document.steps = source.steps.map((step) => cloneStep(step));
  document.groups = source.groups.map((group) => {
    const clone = new WorkflowGroup();
    clone.name = group.name;
    return clone;
  });
  rebuildGroupMembership(document);
  return document;
}

function cloneStep(source: WorkflowStep): WorkflowStep {
  const step = new WorkflowStep();
  step.name = source.name;
  step.stepId = source.stepId;
  step.disabled = source.disabled;
  step.scene = source.scene;
  step.expectedDuration = source.expectedDuration;
  step.stepContext = source.stepContext;
  step.stepInstructions = source.stepInstructions;
  step.backButton = source.backButton;
  step.nextButton = source.nextButton;
  step.nextTarget = source.nextTarget;
  step.isCompletionStep = source.isCompletionStep;
  step.completionCondition =
    source.completionCondition === null ? null : cloneCompletionCondition(source.completionCondition);
  step.groupName = source.groupName;
  step.userInputAllowed = source.userInputAllowed === null ? null : [...source.userInputAllowed];
  step.invalidUserInputAllowed = [...source.invalidUserInputAllowed];
  if (source.attachment !== null) {
    const attachment = new WorkflowStepAttachment();
    attachment.part = source.attachment.part;
    attachment.point = source.attachment.point === null ? null : { ...source.attachment.point };
    step.attachment = attachment;
  }
  if (source.stepView !== null) step.stepView = cloneStepView(source.stepView);
  if (source.media !== null) step.media = cloneMedia(source.media);
  step.triggers = source.triggers.map((trigger) => cloneTrigger(trigger));
  step.scripts = source.scripts.map((script) => cloneScript(script));
  return step;
}

function cloneCompletionCondition(
  source: WorkflowStepCompletionCondition,
): WorkflowStepCompletionCondition {
  const condition = new WorkflowStepCompletionCondition();
  condition.type = source.type;
  condition.delaySeconds = source.delaySeconds;
  condition.invalidType = source.invalidType;
  return condition;
}

/**
 * ⚠ `cloneWorkflowDocument` is a MANUAL field list with no spread, and
 * `applyWorkflowPatch` starts from that clone and then serializes it — so a
 * field omitted here is stripped from the file on every agent patch, silently
 * and with a `changed: true` report. Add every new step field to `cloneStep`.
 */
export function cloneStepView(source: WorkflowStepView): WorkflowStepView {
  const view = new WorkflowStepView();
  view.position = source.position === null ? null : { ...source.position };
  view.pivot = source.pivot === null ? null : { ...source.pivot };
  return view;
}

export function cloneMedia(source: WorkflowStepMedia): WorkflowStepMedia {
  const media = new WorkflowStepMedia();
  media.type = source.type;
  media.fit = source.fit;
  media.url = source.url;
  media.name = source.name;
  media.description = source.description;
  media.transcript = source.transcript;
  media.startTime = source.startTime;
  media.endTime = source.endTime;
  media.loop = source.loop;
  media.autoplay = source.autoplay;
  media.invalidType = source.invalidType;
  media.invalidFit = source.invalidFit;
  return media;
}

export function cloneTrigger(source: WorkflowStepTrigger): WorkflowStepTrigger {
  const trigger = new WorkflowStepTrigger();
  trigger.type = source.type;
  trigger.label = source.label;
  trigger.goTo = source.goTo;
  trigger.invalidText = source.invalidText;
  trigger.invalidType = source.invalidType;
  trigger.invalidRaw = source.invalidRaw;
  return trigger;
}

export function cloneScript(source: WorkflowScript): WorkflowScript {
  const script = new WorkflowScript();
  script.name = source.name;
  script.id = source.id;
  script.trigger = source.trigger;
  script.revert = source.revert;
  script.invalidTrigger = source.invalidTrigger;
  script.invalidRevert = source.invalidRevert;
  script.invalidRaw = source.invalidRaw;
  script.steps = source.steps.map((step) => {
    const clone = new WorkflowScriptStep();
    clone.tool = step.tool;
    clone.args = step.args === null ? null : { ...step.args };
    clone.sayText = step.sayText;
    clone.invalidRaw = step.invalidRaw;
    return clone;
  });
  return script;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function validate(
  document: WorkflowDocument | null,
  catalog?: WorkflowReferenceCatalog | null,
): WorkflowValidationReport {
  const report = new WorkflowValidationReport();
  if (document === null) {
    report.errors.push("The workflow file is empty.");
    return report;
  }

  if (isNullOrWhiteSpace(document.workflowName)) {
    report.errors.push("Missing workflow name (the `workflow.name` key / `# Workflow:` title).");
  }

  if (catalog) validateWorkflowProductReference(document, catalog, report);

  checkPanelsLayout(document, report);

  const playableSteps = getPlayableSteps(document);
  if (document.steps.length === 0) {
    report.errors.push("The workflow has no steps.");
  } else if (playableSteps.length === 0) {
    // An ERROR, because errors ARE the play gate: without one `isWorkflowPlayable`
    // would come back false with nothing on screen saying why, and the Preview
    // button would sit disabled next to a file that looks complete.
    report.errors.push(
      "Every step is disabled, so the workflow has nothing to play. Re-enable at least one step " +
        "(drop its `disabled: true`).",
    );
  } else if (!playableSteps.some((step) => isCompletionStep(step))) {
    report.errors.push(
      "The workflow has no completion step. Mark at least one enabled step " +
        "`is_completion_step: true` so pressing Next there can finish the run.",
    );
  }

  const seenStepNames = new Set<string>();
  for (let stepIndex = 0; stepIndex < document.steps.length; stepIndex++) {
    const step = document.steps[stepIndex];
    if (isNullOrWhiteSpace(step.name)) {
      report.errors.push("A step has no name.");
      continue;
    }

    const stepNameKey = step.name.toLowerCase();
    if (seenStepNames.has(stepNameKey)) {
      report.errors.push(`Duplicate step name "${step.name}".`);
    } else {
      seenStepNames.add(stepNameKey);
    }

    // ONE warning for a step with no authored prose at all, rather than one per
    // field: the two say different things (the panel shows nothing / the guide
    // has only the name to recognise it by) but they are the same authoring
    // gap, and warning twice on every freshly seeded step is noise. A step
    // carrying either one was written deliberately and is left alone.
    if (isNullOrWhiteSpace(step.stepInstructions) && isNullOrWhiteSpace(step.stepContext)) {
      report.warnings.push(
        `Step "${step.name}" has no Instructions and no Context: the panel shows nothing under ` +
          "the step name, the guide has only the name to recognise it by, and the builder has nothing to draft a script from.",
      );
    }

    if (!isNullOrWhiteSpace(step.scene)) {
      report.sceneReferences.push({ key: step.name, value: step.scene!.trim() });
    }

    validateStepScripts(step, stepIndex, report);
    validateStepTriggers(document, step, report);
    checkNextTarget(document, step, stepIndex, report);
    validateStepMedia(step, report);
    validateStepView(step, report);
    if (step.invalidUserInputAllowed.length > 0) {
      report.warnings.push(
        `Step "${step.name}" user_input_allowed has unknown ${
          step.invalidUserInputAllowed.length === 1 ? "entry" : "entries"
        } ${step.invalidUserInputAllowed.map((v) => `"${v}"`).join(", ")} (expected ${USER_INPUT_ALLOWANCES.join(", ")}).`,
      );
    }
    // A parked step is not checked for how the learner gets OUT of it: no
    // learner is ever on it, so "may be a dead end" and "nothing can end the
    // run there" are both false statements about it — and these warnings are a
    // work list the builder agent acts on, where a false entry costs more than
    // a missing one. Everything else about the step is still checked, because
    // it stays editable and must be sound the day it is re-enabled.
    if (!isStepDisabled(step)) {
      checkStepReachability(step, report);
      checkCompletionStep(step, report);
    }
    checkStepCompletionCondition(step, report);
    if (catalog) validateStepProjectReferences(step, stepIndex, catalog, report);
  }

  if (catalog) report.checkedReferences = true;
  report.checkedCommands = isScriptCommandCatalogReady();

  validateGroups(document, report);
  checkBranchFallThrough(document, report);

  if (isNullOrWhiteSpace(document.description)) {
    report.warnings.push("Missing workflow Description.");
  }

  /*
    `lock_ui` freezes an arrangement rather than defining one, so with
    `use_custom_ui` off there is nothing of the WORKFLOW's for it to freeze —
    it pins whatever that particular browser last remembered, which differs per
    viewer and is almost certainly not what an author reaching for a lock meant.
    A warning rather than an error because the flag still does something real,
    and the fix is one line.
  */
  if (isUiLocked(document) && !isCustomUiEnabled(document)) {
    report.warnings.push(
      "lock_ui is on but use_custom_ui is not, so the run freezes whatever arrangement each " +
        "viewer's own browser last remembered rather than one this workflow defines. " +
        "Set use_custom_ui: true and arrange the panels first.",
    );
  }

  // An unrecognised `user_input_allowed` member is a WARNING, not an error:
  // it opens nothing, so the run is still exactly as guided as the rest of the
  // list says, and refusing the file would take a whole workflow offline over
  // one misspelled word. Same rule as an unknown tool id in a script.
  if (document.invalidUserInputAllowed.length > 0) {
    report.warnings.push(
      `Workflow user_input_allowed has unknown ${
        document.invalidUserInputAllowed.length === 1 ? "entry" : "entries"
      } ${document.invalidUserInputAllowed.map((v) => `"${v}"`).join(", ")} (expected ${USER_INPUT_ALLOWANCES.join(", ")}).`,
    );
  }

  if (document.type.length > 0 && normalizeWorkflowType(document.type) === null) {
    report.errors.push(
      `Unknown workflow type "${document.type}" (expected ${WORKFLOW_TYPES.join(", ")}).`,
    );
  }

  if (document.version.length > 0 && document.version !== WORKFLOW_FORMAT_VERSION) {
    // Informational: a frozen version is legitimate, but the builder should
    // know the file predates the format it is being checked against.
    report.warnings.push(
      `Workflow format version "${document.version}" is not the current ${WORKFLOW_FORMAT_VERSION}.`,
    );
  }

  document.knowledgeDocs.forEach((entry, index) => {
    if (entry.isInvalid) {
      report.errors.push(`knowledge.include[${index}] is not a mapping (expected a 'doc' key).`);
    } else if (isNullOrWhiteSpace(entry.doc)) {
      report.errors.push(`knowledge.include[${index}] has no 'doc' id.`);
    } else if (catalog) {
      validateWorkflowKnowledgeDocumentReference(entry.doc, index, catalog, report);
    }
  });

  return report;
}

function validateWorkflowKnowledgeDocumentReference(
  doc: string,
  index: number,
  catalog: WorkflowReferenceCatalog,
  report: WorkflowValidationReport,
): void {
  const documentNameKey = normalizeProductNameKey(doc);
  if (!documentNameKey) return;

  const status = catalog.knowledgeDocs?.get(documentNameKey);
  if (!status) return;
  if (status.state === "found") return;

  const yamlPath = ["workflow", "knowledge", "include", index, "doc"];
  if (status.state === "archived") {
    addIssue(
      report,
      "reference",
      "",
      "knowledge",
      "Archived document",
      `Workflow knowledge document "${status.displayName || doc}" is archived and cannot be used for retrieval.`,
      yamlPath,
    );
    return;
  }

  if (status.state === "missing") {
    addIssue(
      report,
      "reference",
      "",
      "knowledge",
      "Unknown document",
      `Workflow knowledge document "${doc}" does not match a document in the document catalog.`,
      yamlPath,
    );
    return;
  }

  addIssue(
    report,
    "reference",
    "",
    "knowledge",
    "Document check unavailable",
    `Workflow knowledge document "${doc}" could not be checked against the document catalog${
      status.reason ? `: ${status.reason}` : "."
    }`,
    yamlPath,
  );
}

function validateWorkflowProductReference(
  document: WorkflowDocument,
  catalog: WorkflowReferenceCatalog,
  report: WorkflowValidationReport,
): void {
  const product = normalizeProductNameKey(document.product);
  if (!product) return;

  const status = catalog.product;
  if (!status || normalizeProductNameKey(status.productKey) !== product) return;

  if (status.state === "found") return;

  if (status.state === "archived") {
    addIssue(
      report,
      "reference",
      "",
      "product",
      "Archived product",
      `Workflow product "${status.displayName || product}" is archived and cannot be used for document retrieval.`,
      ["workflow", "product"],
    );
    return;
  }

  if (status.state === "missing") {
    addIssue(
      report,
      "reference",
      "",
      "product",
      "Unknown product",
      `Workflow product "${product}" does not match an active product in the product catalog.`,
      ["workflow", "product"],
    );
    return;
  }

  addIssue(
    report,
    "reference",
    "",
    "product",
    "Product check unavailable",
    `Workflow product "${product}" could not be checked against the product catalog${
      status.reason ? `: ${status.reason}` : "."
    }`,
    ["workflow", "product"],
  );
}

/**
 * Validates the optional group layer. Groups are addressable by name (trigger
 * `go_to` targets, the play panel's "n of m" scope), so every group must be
 * nameable and unambiguous — and its member steps must be CONTIGUOUS in the
 * step order, because the authored view renders group headings positionally
 * and play order is the flat step order.
 */
function validateGroups(document: WorkflowDocument, report: WorkflowValidationReport): void {
  const seenGroupNames = new Set<string>();
  for (const group of document.groups) {
    if (isNullOrWhiteSpace(group.name)) {
      report.errors.push("A group has no name.");
      continue;
    }

    const groupNameKey = group.name.toLowerCase();
    if (seenGroupNames.has(groupNameKey)) {
      report.errors.push(`Duplicate group name "${group.name}".`);
    } else {
      seenGroupNames.add(groupNameKey);
    }

    if (group.steps.length === 0) {
      report.warnings.push(`Group "${group.name}" has no steps.`);
    }

    if (document.findStep(group.name) !== null) {
      report.warnings.push(
        `Group "${group.name}" has the same name as a step; navigation targets using that name resolve to the step.`,
      );
    }
  }

  // Contiguity: a group's members must form one run in the steps order.
  const seenClosedGroups = new Set<string>();
  let openGroup = "";
  for (const step of document.steps) {
    const key = step.groupName.toLowerCase();
    if (key === openGroup.toLowerCase()) continue;

    if (seenClosedGroups.has(key) && key.length > 0) {
      report.errors.push(
        `Group "${step.groupName}" has non-contiguous steps; steps of one group must be consecutive (the play order and the authored view both follow the flat step order).`,
      );
    }

    if (openGroup.length > 0) seenClosedGroups.add(openGroup.toLowerCase());
    openGroup = step.groupName;
  }
}

/**
 * Validates a step's trigger buttons: each entry must be well-formed, carry
 * a known type and a label the button can render, and point at a real step
 * or group. A broken trigger is an ERROR (it renders as a button that strands
 * the learner, or not at all), except duplicate labels, which still work but
 * read ambiguously.
 */
function validateStepTriggers(
  document: WorkflowDocument,
  step: WorkflowStep,
  report: WorkflowValidationReport,
): void {
  const seenLabels = new Set<string>();
  for (const trigger of step.triggers) {
    if (trigger.isInvalid) {
      report.errors.push(
        `Step "${step.name}" has a triggers entry that is not a mapping (expected type/label/go_to keys).`,
      );
      continue;
    }

    if (trigger.invalidText.length > 0) {
      report.errors.push(
        `Step "${step.name}" has a trigger line that does not match the grammar: "${trigger.invalidText}" (expected \`Button "<label>" -> <step or group>\`).`,
      );
      continue;
    }

    if (trigger.invalidType.length > 0) {
      report.errors.push(
        `Step "${step.name}" has a trigger with unknown type "${trigger.invalidType}" (expected ${STEP_TRIGGER_TYPES.join(", ")}).`,
      );
      continue;
    }

    if (isNullOrWhiteSpace(trigger.label)) {
      report.errors.push(`Step "${step.name}" has a button trigger without a label.`);
      continue;
    }

    const labelKey = trigger.label.toLowerCase();
    if (seenLabels.has(labelKey)) {
      report.warnings.push(
        `Step "${step.name}" has duplicate trigger button label "${trigger.label}".`,
      );
    } else {
      seenLabels.add(labelKey);
    }

    if (isNullOrWhiteSpace(trigger.goTo)) {
      report.errors.push(
        `Step "${step.name}" trigger button "${trigger.label}" has no go_to target.`,
      );
      continue;
    }

    if (findNavigationTargetStep(document, trigger.goTo) === null) {
      const emptyGroup = document.findGroup(trigger.goTo);
      report.errors.push(
        emptyGroup !== null
          ? `Step "${step.name}" trigger button "${trigger.label}" goes to group "${trigger.goTo}", which has no steps.`
          : `Step "${step.name}" trigger button "${trigger.label}" goes to unknown step or group "${trigger.goTo}".`,
      );
      continue;
    }

    // The target EXISTS but is parked, so the button has nowhere live to go and
    // the play panel hides it. A warning rather than an error: the target is a
    // real step the author can re-enable, and one parked branch must not take
    // the whole workflow offline. Skipped for a parked step's own triggers,
    // which nobody presses either way.
    if (!isStepDisabled(step) && findPlayableNavigationTarget(document, trigger.goTo) === null) {
      report.warnings.push(
        `Step "${step.name}" trigger button "${trigger.label}" goes to "${trigger.goTo}", which is ` +
          "disabled, so the button is hidden during playback. Re-enable that step, point the " +
          "button somewhere else, or drop the button.",
      );
    }
  }
}

/**
 * Every way a step's `next_target` can fail to be a real destination, each a
 * `structure` ERROR addressed to the `Next goes to:` line — so the panel shows
 * it in red and the workflow does not play until it is fixed (user direction,
 * 2026-10-20: the no-Next and disabled cases were proposed as warnings and
 * made errors). A target that cannot be honoured must never quietly become
 * "the next step in the file", because in a branching workflow that is the
 * other branch.
 *
 * The disabled-target case is skipped on a parked step, which nobody presses
 * Next on — the exception a trigger pointing at a parked step already has.
 * The others are structural and checked on a parked step too, so it is sound
 * the day it is re-enabled.
 */
function checkNextTarget(
  document: WorkflowDocument,
  step: WorkflowStep,
  stepIndex: number,
  report: WorkflowValidationReport,
): void {
  const targetName = normalizeNextTarget(step.nextTarget);
  if (targetName === null) return;
  const path = ["steps", stepIndex, "next_target"];
  const issue = (title: string, message: string) =>
    addIssue(report, "structure", step.name, "next_target", title, message, path);

  if (getStepNextButtonLabel(step) === null) {
    issue(
      "Next target without a Next button",
      `Step "${step.name}" has a Next target but no Next button, so nothing can use it. Clear ` +
        "Next goes to, or give the step a Next label.",
    );
  }

  if (isCompletionStep(step)) {
    issue(
      "Ending has a Next target",
      `Step "${step.name}" ends the workflow, so its Next finishes the run and cannot also go to ` +
        `"${targetName}". Clear one of the two.`,
    );
  }

  const target = findNavigationTargetStep(document, targetName);
  if (target === null) {
    issue(
      "Unknown Next target",
      document.findGroup(targetName) !== null
        ? `Step "${step.name}" sends Next to group "${targetName}", which has no steps.`
        : `Step "${step.name}" sends Next to "${targetName}", which is not a step or group in this workflow.`,
    );
    return;
  }

  if (target === step) {
    issue(
      "Next goes nowhere",
      `Step "${step.name}" sends Next back to itself. Pick another step, or clear Next goes to.`,
    );
    return;
  }

  if (!isStepDisabled(step) && findPlayableNavigationTarget(document, targetName) === null) {
    issue(
      "Next target is disabled",
      `Step "${step.name}" sends Next to "${targetName}", which is disabled. Re-enable ` +
        `"${targetName}", or pick another step.`,
    );
  }
}

/**
 * The branch fall-through WARNING: a step whose Next has no target walks, by
 * file order, into a step that a trigger button on ANOTHER step leads to —
 * the first step of a branch, most often the OTHER branch. Before
 * `next_target` existed there was nothing to fix this with but moving steps
 * around; now the fix is one line, so it is worth saying.
 *
 * A warning, not an error: walking into a branch's first step is occasionally
 * meant (a retry loop), and a false error would stop a workflow playing.
 * Playback scope throughout — a parked step neither walks nor is walked into.
 */
function checkBranchFallThrough(document: WorkflowDocument, report: WorkflowValidationReport): void {
  const playable = getPlayableSteps(document);

  // Every step a button leads to, with the buttons leading there in file
  // order — resolved ONCE. `validate` runs on every keystroke in the editor,
  // and the first version scanned every step's buttons for every step, each
  // through a linear name lookup: 695 ms per validation at 1000 steps,
  // against ~8 ms for everything else (measured 2026-10-20).
  const ledTo = new Map<WorkflowStep, Array<{ source: WorkflowStep; label: string }>>();
  for (const source of playable) {
    for (const trigger of source.triggers) {
      if (trigger.isInvalid || isNullOrWhiteSpace(trigger.label)) continue;
      const target = findPlayableNavigationTarget(document, trigger.goTo);
      if (target === null) continue;
      const buttons = ledTo.get(target) ?? [];
      buttons.push({ source, label: trigger.label });
      ledTo.set(target, buttons);
    }
  }
  if (ledTo.size === 0) return;

  for (let i = 0; i + 1 < playable.length; i++) {
    const step = playable[i];
    if (getStepNextButtonLabel(step) === null) continue;
    if (isCompletionStep(step) || normalizeNextTarget(step.nextTarget) !== null) continue;

    const next = playable[i + 1];
    const button = ledTo.get(next)?.find((candidate) => candidate.source !== step);
    if (button === undefined) continue;
    report.warnings.push(
      `Step "${step.name}" has no Next target, so Next goes to "${next.name}", the step the ` +
        `"${button.label}" button on "${button.source.name}" leads to. If that is the other branch, ` +
        "set its Next goes to.",
    );
  }
}

/**
 * Validates a step's optional camera framing.
 *
 * A HALF block — one vector without the other — is the only thing to say here,
 * and it is a WARNING rather than an error for the format's usual reason: a
 * camera framing is cosmetic, and refusing the document would take a whole
 * workflow offline over a step that simply does not move the camera.
 * {@link resolveStepView} already fails open, so the run is correct either way;
 * the warning is what tells the author their capture only half landed.
 *
 * The values themselves are not range-checked. A camera can legitimately sit
 * anywhere — a large-scale scene puts it hundreds of metres out — so there is
 * no number here that is wrong on its face, and a check with no honest
 * threshold is worse than none.
 */
function validateStepView(step: WorkflowStep, report: WorkflowValidationReport): void {
  const view = step.stepView;
  if (view === null) return;

  const missing = view.position === null ? "position" : view.pivot === null ? "pivot" : null;
  if (missing !== null) {
    report.warnings.push(
      `Step "${step.name}" step_view has no ${missing}, so it is incomplete and the camera will not ` +
        "move on this step. Capture the view again with get_camera_view and send both position and pivot.",
    );
  }
}

/**
 * Validates a step's optional media. The URL is the only required part — media
 * with nothing to fetch renders an empty panel beside the step, which reads as
 * a broken workflow rather than as no media. The video-only fields are a
 * WARNING on an image rather than being ignored: the author plainly meant the
 * step to play something, and silently dropping a transcript is worse than
 * saying the type disagrees with it.
 */
function validateStepMedia(step: WorkflowStep, report: WorkflowValidationReport): void {
  const media = step.media;
  if (media === null) return;

  if (media.invalidType.length > 0) {
    report.errors.push(
      `Step "${step.name}" media has unknown type "${media.invalidType}" (expected ${MEDIA_TYPES.join(", ")}).`,
    );
  }

  if (media.invalidFit.length > 0) {
    report.errors.push(
      `Step "${step.name}" media has unknown fit "${media.invalidFit}" (expected ${MEDIA_FITS.join(", ")}).`,
    );
  }

  if (isNullOrWhiteSpace(media.url)) {
    report.errors.push(`Step "${step.name}" media has no url.`);
  } else if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(media.url.trim()) && !media.url.startsWith("/")) {
    report.warnings.push(
      `Step "${step.name}" media url "${media.url.trim()}" does not look like a URL; use an https:// address or a campfire:// reference copied from the Asset Library's Media tab.`,
    );
  }

  const kind = resolveMediaType(media);
  const timedFields: Array<[string, string]> = [
    ["start", media.startTime],
    ["end", media.endTime],
  ];

  if (kind === "image") {
    const videoOnly = [
      ...timedFields.filter(([, value]) => !isNullOrWhiteSpace(value)).map(([key]) => key),
      ...(isNullOrWhiteSpace(media.transcript) ? [] : ["transcript"]),
      // Any STATED loop, `false` included: it is behaviourally the same as
      // absent, but writing a video-only key on an image is the signal this
      // warning exists for — the type is probably wrong.
      ...(media.loop === null ? [] : ["loop"]),
      ...(media.autoplay === null ? [] : ["autoplay"]),
    ];
    if (videoOnly.length > 0) {
      report.warnings.push(
        `Step "${step.name}" media is an image but carries ${videoOnly.join(" / ")}, which only apply to a video.`,
      );
    }
    return;
  }

  let start: number | null = null;
  let end: number | null = null;
  for (const [key, value] of timedFields) {
    if (isNullOrWhiteSpace(value)) continue;
    const seconds = parseMediaTimecodeSeconds(value);
    if (seconds === null) {
      report.errors.push(
        `Step "${step.name}" media ${key} "${value.trim()}" is not a timecode (expected seconds, mm:ss, or hh:mm:ss).`,
      );
      continue;
    }
    if (key === "start") start = seconds;
    else end = seconds;
  }

  if (start !== null && end !== null && end <= start) {
    report.errors.push(
      `Step "${step.name}" media end (${media.endTime.trim()}) is not after its start (${media.startTime.trim()}).`,
    );
  }
}

/**
 * Flags steps the learner can get stuck in: the step has no Next button (its
 * `next_button` label is empty) and nothing else gives them a visible way to
 * move on — no trigger button, and no script that waits for a response or
 * asks them to continue. A warning, not an error, so the workflow-builder can
 * propose a fix (ask the learner to continue, add a trigger button, or give the
 * Next button a label) rather than being hard-blocked.
 */
function checkStepReachability(step: WorkflowStep, report: WorkflowValidationReport): void {
  if (getStepNextButtonLabel(step) !== null) return;

  // Empty steps are covered by the "no Instructions and no Context"
  // warning. Emptiness has to test BOTH prose fields, or a step carrying only
  // instructions would collect a dead-end warning on top of one that was never
  // raised.
  if (
    isNullOrWhiteSpace(step.stepInstructions) &&
    isNullOrWhiteSpace(step.stepContext) &&
    step.scripts.length === 0
  ) {
    return;
  }

  const hasTriggerButton = stepHasTriggerButtons(step);
  const hasWaitForResponse = step.scripts.some((script) =>
    script.steps.some(
      (entry) =>
        equalsIgnoreCase(entry.tool, AgentToolNames.WaitFor) ||
        containsAnyKeyword(entry.sayText, K_RESPONSE_STEP_KEYWORDS),
    ),
  );

  if (hasTriggerButton || hasWaitForResponse) return;

  report.warnings.push(
    `Step "${step.name}" may be a dead end: it has no Next button and nothing asks the learner to respond or press a button, ` +
      "so there is no visible way to continue or finish. Ask the learner to continue (a wait_for user_response step), add a trigger button, or give the step's next_button a label.",
  );
}

/**
 * Flags a completion step the learner can never finish FROM: `is_completion_step`
 * only ever fires on the Next press, so a step with no Next button carries an
 * ending nothing can reach. A warning rather than an error, because the fix is
 * one label and the run is otherwise fine — the step just behaves like any
 * other, which is precisely what makes the flag invisible without this line.
 *
 * Note the two checks read the same field for opposite reasons and must not be
 * merged: {@link checkStepReachability} asks whether the learner can get OUT of
 * a step at all (and stays quiet when a trigger button or a wait gives them a
 * way), while this one asks whether a stated ending is reachable — a trigger
 * button navigates elsewhere and can never end the run, so it is no answer here.
 */
function checkCompletionStep(step: WorkflowStep, report: WorkflowValidationReport): void {
  if (!isCompletionStep(step)) return;
  if (getStepNextButtonLabel(step) !== null) return;

  report.warnings.push(
    `Step "${step.name}" is marked is_completion_step but has no Next button, so nothing can ` +
      "end the run there. Give its next_button a label (\"Finish\" reads well on an ending), or " +
      "drop is_completion_step.",
  );
}

/**
 * Flags a `completion_condition` that cannot do what it says.
 *
 * Every branch here is something that would otherwise be SILENT: the resolver
 * fails open, so a broken condition leaves Next available exactly as if the
 * author had never written one, and the only trace is the key sitting in the
 * file doing nothing.
 *
 * The two errors are the ones where the author's intent cannot be honoured at
 * all (an unrecognised kind; a delay with no length). The warnings are where it
 * is honoured but reaches nothing — a delay on a condition that is not one, a
 * script gate on a step with no script, a video gate on a step with no video,
 * and a gate on a step with no Next button to gate.
 */
function checkStepCompletionCondition(
  step: WorkflowStep,
  report: WorkflowValidationReport,
): void {
  const condition = step.completionCondition;
  if (condition === null) return;

  if (condition.invalidType.length > 0) {
    report.errors.push(
      `Step "${step.name}" completion_condition has unknown type "${condition.invalidType}" ` +
        `(expected ${COMPLETION_CONDITIONS.join(", ")}).`,
    );
    return;
  }

  if (condition.type === null) {
    report.errors.push(
      `Step "${step.name}" completion_condition needs a 'type' ` +
        `(${COMPLETION_CONDITIONS.join(" or ")}).`,
    );
    return;
  }

  if (condition.type === "delay") {
    if (condition.delaySeconds === null) {
      report.errors.push(
        `Step "${step.name}" completion_condition is a delay but has no 'delay_seconds', so ` +
          "nothing says when Next becomes available. Give it a number of seconds, or use " +
          "type: script_finished.",
      );
    } else if (!Number.isFinite(condition.delaySeconds) || condition.delaySeconds <= 0) {
      report.errors.push(
        `Step "${step.name}" completion_condition delay_seconds (${condition.delaySeconds}) must ` +
          "be greater than 0.",
      );
    } else if (condition.delaySeconds > MAX_COMPLETION_DELAY_SECONDS) {
      report.warnings.push(
        `Step "${step.name}" completion_condition waits ${condition.delaySeconds}s, which is ` +
          `clamped to ${MAX_COMPLETION_DELAY_SECONDS}s at playback — a learner held on one step ` +
          "for longer than that reads as a broken button.",
      );
    }
  } else if (condition.delaySeconds !== null) {
    report.warnings.push(
      `Step "${step.name}" completion_condition is ${condition.type} but carries delay_seconds, ` +
        "which only applies to a delay condition.",
    );
  }

  if (condition.type === "script_finished" && step.findScriptByTrigger("on_start") === null) {
    report.warnings.push(
      `Step "${step.name}" waits for its script to finish but has no on_start script, so there is ` +
        "nothing to wait for — Next is available straight away. Add the script, or drop the " +
        "completion_condition.",
    );
  }

  if (condition.type === "video_finished" && !stepHasPlayableVideo(step)) {
    report.warnings.push(
      `Step "${step.name}" waits for its video to finish but shows no video, so there is nothing ` +
        "to wait for — Next is available straight away. Give the step a media block whose url is " +
        "a video, or drop the completion_condition.",
    );
  }

  if (getStepNextButtonLabel(step) === null) {
    report.warnings.push(
      `Step "${step.name}" has a completion_condition but no Next button, so there is nothing for ` +
        "it to hold back. Give its next_button a label, or drop the completion_condition.",
    );
  }
}

function containsAnyKeyword(text: string, keywords: ReadonlyArray<string>): boolean {
  if (isNullOrWhiteSpace(text)) return false;
  for (const keyword of keywords) {
    if (containsIgnoreCase(text, keyword)) return true;
  }
  return false;
}

/**
 * Validates a step's self-contained prepared scripts: ids unique, tool ids
 * real, steps well-formed. Malformed entries (non-mapping scripts, steps that
 * are neither say nor tool) are ERRORS naming the entry — they round-trip
 * verbatim through re-serialization, so the error survives instead of the
 * data being silently destroyed.
 */
function validateStepScripts(
  step: WorkflowStep,
  stepIndex: number,
  report: WorkflowValidationReport,
): void {
  if (
    (!isNullOrWhiteSpace(step.stepInstructions) || !isNullOrWhiteSpace(step.stepContext)) &&
    step.scripts.length === 0
  ) {
    report.warnings.push(
      `Step "${step.name}" has authored prose but no prepared scripts yet (the workflow-builder writes them).`,
    );
  }

  const seenScriptIds = new Set<string>();
  step.scripts.forEach((script, index) => {
    if (script.isInvalid) {
      report.errors.push(
        `Step "${step.name}" scripts[${index}] is not a mapping (expected id/trigger/steps keys).`,
      );
      return;
    }

    const label = isNullOrWhiteSpace(script.name) ? script.id : script.name;
    if (isNullOrWhiteSpace(script.id)) {
      report.errors.push(`Step "${step.name}" has a script "${label}" without an id.`);
    } else {
      const scriptIdKey = script.id.toLowerCase();
      if (seenScriptIds.has(scriptIdKey)) {
        report.errors.push(`Step "${step.name}" has duplicate script id "${script.id}".`);
      } else {
        seenScriptIds.add(scriptIdKey);
      }
    }

    if (script.steps.length === 0) {
      report.warnings.push(`Step "${step.name}" script "${label}" has no steps.`);
    }

    if (script.invalidTrigger.length > 0) {
      report.errors.push(
        `Step "${step.name}" script "${label}" has an unknown trigger "${script.invalidTrigger}" (expected ${SCRIPT_TRIGGERS.join(", ")}).`,
      );
    }

    if (script.invalidRevert.length > 0) {
      report.errors.push(
        `Step "${step.name}" script "${label}" has an unknown revert "${script.invalidRevert}" (expected ${SCRIPT_REVERTS.join(", ")}).`,
      );
    }

    script.steps.forEach((scriptStep, scriptStepIndex) => {
      if (scriptStep.isSay) {
        // A say that defers to instructions the step does not have speaks
        // NOTHING — the runner skips the line rather than handing the model an
        // empty one to improvise over. Silent otherwise, which is exactly the
        // failure a warning has to carry: the script looks like it narrates the
        // step and plays without a word.
        if (
          isSayStepInstructionsToken(scriptStep.sayText) &&
          isNullOrWhiteSpace(step.stepInstructions)
        ) {
          report.warnings.push(
            `Step "${step.name}" script "${label}" says ${SAY_STEP_INSTRUCTIONS_TOKEN}, but the step has ` +
              "no Instructions, so that line speaks nothing. Write the step's Instructions, or give the say its own text.",
          );
        }
        return;
      }

      if (scriptStep.isInvalid || scriptStep.tool.length === 0) {
        report.errors.push(
          `Step "${step.name}" script "${label}" has a step that is neither a 'say' nor a 'tool'.`,
        );
        return;
      }

      if (equalsIgnoreCase(scriptStep.tool, AgentToolNames.PlayVideo)) {
        const toolPath = ["steps", stepIndex, "scripts", index, "steps", scriptStepIndex, "tool"];
        if (!stepHasPlayableVideo(step)) {
          addIssue(
            report,
            "command",
            step.name,
            "scripts",
            "No video to play",
            `Step "${step.name}" script "${label}" calls play_video, but this step does not show a video. Add video media to the step, or remove the command.`,
            toolPath,
          );
        } else if (step.media?.autoplay === true) {
          addIssue(
            report,
            "command",
            step.name,
            "scripts",
            "Video already autoplays",
            `Step "${step.name}" script "${label}" calls play_video and its media also sets autoplay: true. These are alternatives: keep autoplay for playback on arrival, or keep play_video for playback at this point in the script.`,
            toolPath,
          );
        }
      }

      validateScriptCommandUsage(scriptStep, {
        step: step.name,
        where: `Step "${step.name}" script "${label}"`,
        stepPath: ["steps", stepIndex, "scripts", index, "steps", scriptStepIndex],
        report,
      });
    });
  });
}

/**
 * Checks one script step against the tool registry: that the command exists,
 * that it is something a script can run, and that its arguments are the ones
 * the tool declares — the right names, the right types, values inside the
 * declared enums, and nothing required left out.
 *
 * Findings are ADDRESSED issues, so the panel can put them on the row that
 * carries the mistake. They are what the author sees; the rest of `validate`'s
 * structural advice reaches the workflow-builder role through the report and
 * is deliberately not surfaced as a panel suggestion. The line between the two
 * is that these are facts checkable against the registry — the name exists or
 * it does not — rather than opinions about how the lesson should read.
 *
 * WARNINGS, not errors, and that is the load-bearing decision. An error
 * refuses the whole document: the panel drops to the raw editor and the
 * workflow stops playing, so every error is a way for one bad line to take a
 * project's whole training offline. These conditions do not justify that, for
 * two reasons:
 *
 *  - The runtime is more tolerant than the declared schema. Executors fold enum
 *    values (trim, lowercase, strip separators) and accept documented aliases —
 *    `semanticColor: red` and `yellow` both play, and are named in the tool's
 *    own description. Refusing them would refuse a file that works.
 *  - A wrong argument breaks ONE step. A refusal breaks the workflow.
 */
function validateScriptCommandUsage(
  scriptStep: WorkflowScriptStep,
  context: {
    /** Step name, the issue's address. */
    readonly step: string;
    /** Human prefix for the message ("Step X script Y"). */
    readonly where: string;
    /** YAML path of this script step, without a trailing key. */
    readonly stepPath: ReadonlyArray<string | number>;
    readonly report: WorkflowValidationReport;
  },
): void {
  const { step, where, stepPath, report } = context;
  const toolPath = [...stepPath, "tool"];
  const argsPath = [...stepPath, "args"];

  const flag = (title: string, message: string, path: ReadonlyArray<string | number>): void => {
    addIssue(report, "command", step, "scripts", title, message, path);
  };

  const command = getScriptCommandSpec(scriptStep.tool);
  if (command === null) {
    // Folded exactly as `getScriptCommandSpec` folds, or a quoted id with a
    // stray space would resolve to nothing here and be called a typo there.
    if (!K_VALID_TOOL_IDS.has(scriptStep.tool.trim().toLowerCase())) {
      // No such tool id anywhere in Campfire — a typo, and the one case that
      // can be called wrong with certainty whether or not the registry is up.
      flag(
        "Unknown tool",
        `${where} uses unknown tool "${scriptStep.tool}". It is not a Campfire tool id, so the step will fail.`,
        toolPath,
      );
    } else if (isScriptCommandCatalogReady()) {
      // A real id this build does not register (the web registry is a
      // deliberate subset). The executor answers `tool_not_supported`, so the
      // step fails for every learner — worth saying, and worth saying
      // differently from a typo, because the fix is different.
      flag(
        "Tool not available",
        `${where} uses "${scriptStep.tool}", which this client does not support. The step will fail when it plays.`,
        toolPath,
      );
    }
    // Otherwise the id is real and the registry has not been built yet
    // (headless parse, or before AI startup): we cannot judge, so say nothing.
    return;
  }

  if (!isAuthorableScriptCommand(command.name)) {
    flag(
      "Not a script command",
      isScriptContextTool(command.name)
        ? `${where} calls "${scriptStep.tool}", which reads context for whoever is authoring — as a ` +
          `step it returns text to nobody. Resolve the target while authoring and write the result ` +
          `into another step's args.`
        : isStructuralScriptCommand(command.name)
          ? `${where} calls "${scriptStep.tool}", which rewrites project structure and cannot run ` +
            `inside Workflow's transient playback scope. Replace it with a view/presentation command.`
        : `${where} calls "${scriptStep.tool}", which is not a command a workflow script can run.`,
      toolPath,
    );
    return;
  }

  const args = scriptStep.args ?? {};

  for (const [key, value] of Object.entries(args)) {
    const arg = command.properties.get(key);
    if (arg === undefined) {
      // Unknown keys are refused by `rejectUnknownToolArgs` when the document
      // is parsed, so in the normal path this is unreachable. It stays because
      // a document can also reach `validate` from an in-memory edit that never
      // went through the parser, and silently ignoring an argument the tool
      // will never read is exactly the failure this whole pass exists to end.
      flag(
        "Unknown argument",
        `${where}: ${command.name} has no argument "${key}"` +
          (command.properties.size === 0
            ? " (it takes none)."
            : ` (expected ${[...command.properties.keys()].join(", ")}).`),
        [...argsPath, key],
      );
      continue;
    }

    if (!satisfiesArgType(value, arg)) {
      const expected = arg.type === "array" ? `a list of ${arg.itemType || "string"}` : `a ${arg.type}`;
      flag(
        "Wrong argument type",
        `${where}: ${command.name}'s "${key}" should be ${expected}.`,
        [...argsPath, key],
      );
      continue;
    }

    if (arg.enumValues.length > 0) {
      const candidates = Array.isArray(value) ? value : [value];
      candidates.forEach((candidate, candidateIndex) => {
        if (typeof candidate !== "string") return;
        // Folded like the executors do, so a difference of case or a separator
        // is not reported as a problem — it is not one.
        if (arg.enumValues.some((allowed) => foldEnumValue(allowed) === foldEnumValue(candidate))) {
          return;
        }
        flag(
          "Unknown value",
          `${where}: "${candidate}" is not a documented value for ${command.name}'s "${key}" ` +
            `(${arg.enumValues.join(", ")}).`,
          Array.isArray(value) ? [...argsPath, key, candidateIndex] : [...argsPath, key],
        );
      });
    }
  }

  for (const name of command.required) {
    if (name in args) continue;
    flag(
      "Missing argument",
      `${where}: ${command.name} needs "${name}".`,
      // Point at `args:` when there is one to point at; a step with no args at
      // all has only its `tool:` row to carry the mark.
      scriptStep.args === null ? toolPath : argsPath,
    );
  }
}/** Executor-equivalent fold: trim, lowercase, drop separators. */
function foldEnumValue(value: string): string {
  return value.trim().toLowerCase().replace(/[-_\s]/g, "");
}

/**
 * Whether a YAML value satisfies a declared argument type.
 *
 * TOLERANT in exactly the directions the executors are, because reporting a
 * value the runtime handles is a false alarm: a bare scalar is read as a
 * one-element list (`getStringList`), and numbers and booleans are parsed out
 * of strings (`getNumber` / `getBool`).
 */
function satisfiesArgType(value: unknown, arg: ScriptCommandArg): boolean {
  if (arg.type === "" || value === null || value === undefined) return true;

  switch (arg.type) {
    case "array":
      return Array.isArray(value)
        ? arg.itemType === "" || value.every((entry) => satisfiesScalarType(entry, arg.itemType))
        : satisfiesScalarType(value, arg.itemType === "" ? "string" : arg.itemType);
    case "object":
      return typeof value === "object" && !Array.isArray(value);
    default:
      return satisfiesScalarType(value, arg.type);
  }
}

function satisfiesScalarType(value: unknown, type: string): boolean {
  switch (type) {
    case "string":
      // YAML types an unquoted `0,1,2` as a string already; a bare `0` part
      // path arrives as a number and the executor coerces it.
      return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
    case "number":
      // `Number.parseFloat`, matching `AgentToolExecutorModel.getNumber` —
      // `Number()` is stricter and would report values the executor reads.
      return (
        typeof value === "number" ||
        (typeof value === "string" && Number.isFinite(Number.parseFloat(value)))
      );
    case "boolean":
      return (
        typeof value === "boolean" ||
        (typeof value === "string" && /^(true|false)$/i.test(value.trim()))
      );
    default:
      return true;
  }
}

// ---------------------------------------------------------------------------
// Project-reference checks (catalog required)
// ---------------------------------------------------------------------------

/**
 * Arg keys that carry a part path (display-name `Robodog/Fan` or index `0,1,2`).
 * Mirrors the Spark look-at scan in `AIOrchestratorModel` plus the extra keys
 * workflow scripts actually write (`partPath`, `partPaths`, `partPathsToReset`,
 * `modelName`).
 */
const K_PART_PATH_ARG_KEYS: ReadonlyArray<string> = [
  "partPath",
  "partPaths",
  "partPathsToMove",
  "partPathsToHide",
  "partPathsToUnhide",
  "partPathsToIsolate",
  "partPathsToSelect",
  "partPathsToFocus",
  "partPathsToLookAt",
  "partPathsToHighlight",
  "partPathsToClearHighlight",
  "partPathsToSpin",
  "partPathsToClearSpin",
  "partPathsToColor",
  "partPathsToRename",
  "partPathsToReset",
  "targetPartPath",
  "modelPathsToExplode",
  "modelPaths",
  "modelName",
];

const K_INDEX_PATH = /^\d+(,\d+)*$/;

function validateStepProjectReferences(
  step: WorkflowStep,
  stepIndex: number,
  catalog: WorkflowReferenceCatalog,
  report: WorkflowValidationReport,
): void {
  const sceneRef = step.scene?.trim() ?? "";
  let sceneIndex: WorkflowScenePartIndex | null | undefined;
  const stepPath = ["steps", stepIndex] as const;

  if (sceneRef.length > 0) {
    const scene = resolveCatalogScene(catalog, sceneRef);
    if (scene === undefined) {
      addIssue(
        report,
        "reference",
        step.name,
        "scene",
        "Unknown scene",
        `Step "${step.name}" references scene "${sceneRef}", which is not in this project.`,
        [...stepPath, "scene"],
      );
      // Still check parts against loaded scenes so a garbage `attachment.part`
      // is not silent just because `scene` is also wrong.
      sceneIndex = unionLoadedPartIndex(catalog);
    } else if (!scene.loaded) {
      // Target scene is not resident, so its parts are unverifiable here. A
      // part path is SCENE-RELATIVE: an index path (`0,1,20`) numbers roots off
      // that scene's own `getEntities` order, and the same model sits at a
      // different root index per scene. Measuring it against whichever scenes
      // happen to be resident reported a whole valid file as unknown parts.
      // Unity validates no part paths at all; the checks re-run for real on
      // `onLoadSceneCompleted`.
      sceneIndex = null;
    } else {
      sceneIndex = catalog.partsByScene.get(scene.id.toLowerCase()) ?? emptyPartIndex();
    }
  } else {
    sceneIndex = unionLoadedPartIndex(catalog);
  }

  if (sceneIndex !== null && sceneIndex !== undefined) {
    const attachmentPart = step.attachment?.part.trim() ?? "";
    if (attachmentPart.length > 0 && !partExistsInIndex(attachmentPart, sceneIndex)) {
      const where = partWherePhrase(sceneRef, catalog);
      addIssue(
        report,
        "reference",
        step.name,
        "attachment",
        "Unknown part",
        `Step "${step.name}" attachment part "${attachmentPart}" was not found ${where}.`,
        [...stepPath, "attachment", "part"],
      );
    }
  }

  step.scripts.forEach((script, scriptIndex) => {
    if (script.isInvalid) return;
    const label = isNullOrWhiteSpace(script.name) ? script.id : script.name;
    script.steps.forEach((scriptStep, scriptStepIndex) => {
      if (scriptStep.isSay || scriptStep.isInvalid) return;
      const args = scriptStep.args;
      const argsPath = [...stepPath, "scripts", scriptIndex, "steps", scriptStepIndex, "args"];

      if (sceneIndex !== null && sceneIndex !== undefined) {
        for (const hit of collectPartPathArgs(args)) {
          if (partExistsInIndex(hit.value, sceneIndex)) continue;
          const where = partWherePhrase(sceneRef, catalog);
          addIssue(
            report,
            "reference",
            step.name,
            "scripts",
            "Unknown part",
            `Step "${step.name}" script "${label}" references unknown part "${hit.value}" ${where}.`,
            hit.index === null ? [...argsPath, hit.key] : [...argsPath, hit.key, hit.index],
          );
        }
      }

      if (equalsIgnoreCase(scriptStep.tool, AgentToolNames.GoToScene)) {
        for (const sceneNumber of collectSceneNumbers(args)) {
          if (sceneNumber >= 1 && sceneNumber <= catalog.scenes.length) continue;
          addIssue(
            report,
            "reference",
            step.name,
            "scripts",
            "Unknown scene number",
            `Step "${step.name}" script "${label}" go_to_scene sceneNumber ${sceneNumber} is out of range (this project has ${catalog.scenes.length} scene${catalog.scenes.length === 1 ? "" : "s"}).`,
            [...argsPath, "sceneNumber"],
          );
        }
      }
    });
  });
}

/**
 * Resolves a step `scene` value against the catalog. Order: id, then full
 * name (case-insensitive), then 1-based index. A scene named "1" therefore
 * wins over index 1. Empty string is not a reference.
 */
export function resolveCatalogScene(
  catalog: WorkflowReferenceCatalog,
  ref: string,
): WorkflowCatalogScene | undefined {
  return resolveWorkflowSceneReference(catalog.scenes, ref);
}

/** Shared validator/runtime resolution for a workflow step's scene reference. */
export function resolveWorkflowSceneReference<
  TScene extends { readonly id: string; readonly name: string; readonly index: number },
>(
  scenes: ReadonlyArray<TScene>,
  ref: string,
): TScene | undefined {
  const key = ref.trim();
  if (key.length === 0) return undefined;

  const lower = key.toLowerCase();
  const byId = scenes.find((scene) => scene.id.toLowerCase() === lower);
  if (byId) return byId;

  const byName = scenes.find((scene) => scene.name.toLowerCase() === lower);
  if (byName) return byName;

  if (/^\d+$/.test(key)) {
    const index = Number(key);
    return scenes.find((scene) => scene.index === index);
  }

  return undefined;
}

function partWherePhrase(sceneRef: string, catalog: WorkflowReferenceCatalog): string {
  if (sceneRef.length === 0) return "in any loaded scene";
  const scene = resolveCatalogScene(catalog, sceneRef);
  if (scene === undefined) return `in any loaded scene (step scene "${sceneRef}" is not in this project)`;
  if (!scene.loaded) return `in any loaded scene (scene "${sceneRef}" is not loaded yet)`;
  return `in scene "${sceneRef}"`;
}

function emptyPartIndex(): WorkflowScenePartIndex {
  return { displayPaths: new Set(), indexPaths: new Set() };
}

function unionLoadedPartIndex(catalog: WorkflowReferenceCatalog): WorkflowScenePartIndex | null {
  if (catalog.partsByScene.size === 0) return null;
  const displayPaths = new Set<string>();
  const indexPaths = new Set<string>();
  for (const index of catalog.partsByScene.values()) {
    for (const path of index.displayPaths) displayPaths.add(path);
    for (const path of index.indexPaths) indexPaths.add(path);
  }
  return { displayPaths, indexPaths };
}

function partExistsInIndex(path: string, index: WorkflowScenePartIndex): boolean {
  const trimmed = path.trim();
  if (trimmed.length === 0) return true;
  if (K_INDEX_PATH.test(trimmed) && index.indexPaths.has(trimmed)) return true;
  return index.displayPaths.has(trimmed.toLowerCase());
}

function collectPartPathArgs(args: Record<string, unknown> | null): Array<{
  key: string;
  value: string;
  index: number | null;
}> {
  if (args === null) return [];
  const out: Array<{ key: string; value: string; index: number | null }> = [];
  for (const key of K_PART_PATH_ARG_KEYS) {
    const value = args[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0) out.push({ key, value: trimmed, index: null });
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === "string" && item.trim().length > 0) {
          out.push({ key, value: item.trim(), index });
        }
      });
    }
  }
  return out;
}

function collectSceneNumbers(args: Record<string, unknown> | null): number[] {
  if (args === null) return [];
  const value = args.sceneNumber;
  if (typeof value === "number" && Number.isFinite(value)) return [value];
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return [parsed];
  }
  return [];
}

/**
 * Records one addressed issue, de-duplicated by its own text.
 *
 * The same unresolved part legitimately appears in several args of one script,
 * and one message repeated is one problem to the reader — but a `command`
 * issue and a `reference` issue that happen to read alike are different
 * problems, so the kind is part of the key.
 */
function addIssue(
  report: WorkflowValidationReport,
  kind: WorkflowIssueKind,
  step: string,
  field: WorkflowIssueField,
  title: string,
  message: string,
  yamlPath: ReadonlyArray<string | number>,
): void {
  if (
    report.issues.some(
      (issue) =>
        issue.kind === kind &&
        issue.step === step &&
        issue.field === field &&
        issue.message === message,
    )
  ) {
    return;
  }
  report.issues.push({ kind, step, field, title, message, yamlPath });
  // A `structure` issue stops the workflow playing, so it is an ERROR — the
  // play gate reads `errors` and nothing else.
  (kind === "structure" ? report.errors : report.warnings).push(message);
}
